import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import MarkdownRenderer from '../components/common/MarkdownRenderer';
import ToolActivityBlock from '../components/common/ToolActivityBlock';
import { parseToolActivity } from '../utils/parseToolActivity';
import AvatarWithFallback from '../components/common/AvatarWithFallback';
import { formatDateTime } from '../utils/dateUtils';
import { areMessagesSimilar } from '../utils/messageSimilarity';
import SplitViewConversation from '../components/prd-workflow/SplitViewConversation';
import SplitViewUserStories from '../components/prd-workflow/SplitViewUserStories';
import SplitViewUserRoles from '../components/prd-workflow/SplitViewUserRoles';
import SplitViewWireframes from '../components/prd-workflow/SplitViewWireframes';
import SplitViewDesigns from '../components/prd-workflow/SplitViewDesigns';
import SplitViewProcessFlows from '../components/prd-workflow/SplitViewProcessFlows';
import SplitViewArchitecture from '../components/prd-workflow/SplitViewArchitecture';
import SplitViewDevelopment from '../components/prd-workflow/SplitViewDevelopment';
import SplitViewDeployment from '../components/prd-workflow/SplitViewDeployment';
import SplitViewUnitTesting from '../components/prd-workflow/SplitViewUnitTesting';
import SplitViewUserPermissions from '../components/prd-workflow/SplitViewUserPermissions';
import SplitViewSecurityTesting from '../components/prd-workflow/SplitViewSecurityTesting';
import SplitViewSecurityGuidelines from '../components/prd-workflow/SplitViewSecurityGuidelines';
import SplitViewCICDPipeline from '../components/prd-workflow/SplitViewCICDPipeline';
import SplitViewFunctionalTesting from '../components/prd-workflow/SplitViewFunctionalTesting';
import SplitViewAutomatedTesting from '../components/prd-workflow/SplitViewAutomatedTesting';
import SplitViewAutomationTesting from '../components/prd-workflow/SplitViewAutomationTesting';
import SplitViewPerformanceTesting from '../components/prd-workflow/SplitViewPerformanceTesting';
import SplitViewGitGuidelines from '../components/prd-workflow/SplitViewGitGuidelines';
import SplitViewVersionControl from '../components/prd-workflow/SplitViewVersionControl';
import SplitViewFieldValidations from '../components/prd-workflow/SplitViewFieldValidations';
import SplitViewTechStack from '../components/prd-workflow/SplitViewTechStack';
import SplitViewAPIGuidelines from '../components/prd-workflow/SplitViewAPIGuidelines';
import SplitViewProjectInfoConversation from '../components/prd-workflow/SplitViewProjectInfoConversation';
import SplitViewDesignReferences from '../components/prd-workflow/SplitViewDesignReferences';
import SplitViewIntegration from '../components/prd-workflow/SplitViewIntegration';
import VisualLinksPanel from '../components/prd-workflow/VisualLinksPanel';
import IsolatedChatInput from '../components/atoms/IsolatedChatInput';
import SpeechListeningAnimation from '../components/common/SpeechListeningAnimation';
import PermissionGate from '../components/common/PermissionGate';
import conversationService from '../services/conversationService';
import taskValidatedItemService from '../services/taskValidatedItemService';
import fileUploadService from '../services/fileUploadService';
import MarkdownFileImport from '../components/prd-workflow/MarkdownFileImport';
import UniversalAccordion from '../components/prd-workflow/UniversalAccordion';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Badge,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Icon,
  Fab,
} from '@mui/material';
import ConversationLayout from '../components/layout/ConversationLayout';
import AppDialog from '../components/layout/AppDialog';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Mic as MicIcon,
  Translate as LanguageIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  MenuBook as MenuBookIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CancelIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import useConversation from '../hooks/useConversation';
import useNotification from '../hooks/useNotification';
import projectService from '../services/projectService';
import useAgentNames from '../hooks/useAgentNames';

// Styled components
const ChatMessageContainer = styled(Box)(({ theme, sender }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start', // Always align messages to the left
  marginBottom: theme.spacing(4), // Increased spacing between messages
  width: '100%'
}));

const ChatBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'sender' && prop !== 'isStreaming'
})(({ theme, sender, isStreaming }) => ({
  padding: theme.spacing(2),
  maxWidth: '97.4%', // Increased max width
  width: 'auto',
  borderRadius: '0px',
  backgroundColor: sender === 'user' ? 'rgba(234, 240, 246, 0.7)' : 'rgba(250, 250, 250, 0.7)',
  color: theme.palette.text.primary,
  wordBreak: 'break-word',
  boxShadow: 'none',
  border: 'none',
  marginBottom: theme.spacing(1),
  borderLeft: sender === 'user' ? '3px solid rgba(25, 118, 210, 0.5)' : 'none',
  ...(isStreaming && {
    backgroundColor: 'rgba(244, 250, 244, 0.6)',
    borderLeft: '2px solid rgba(76, 175, 80, 0.5)'
  }),
  [theme.breakpoints.down('sm')]: {
    maxWidth: '95%',
  }
}));

const ChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '97%',
  position: 'relative',
  alignItems: 'center',
  width: '99%',
  margin: 'auto',
  padding: '0',
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
}));

const MessageList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  scrollBehavior: 'smooth',
  width: '97.4%',
  maxWidth: '97.4%',
  marginLeft: 'auto',
  backgroundColor: 'transparent',
  borderTopLeftRadius: '8px',
  borderTopRightRadius: '8px',
  maxHeight: 'calc(95vh - 180px)', // Ensure it doesn't overflow the parent

  // Custom scrollbar styles
  '&::-webkit-scrollbar': {
    width: '8px',
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    marginTop: '5px',
    marginBottom: '5px',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.4)',
    },
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: 'transparent',
  },

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(1),
  }
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  position: 'sticky',
  bottom: 0,
  width: '97.4%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '97.4%',
  mx: 'auto',
  borderRadius: '0 0 8px 8px',
  backdropFilter: 'blur(5px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3)
  },
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(1)
  }
}));

const FileUploadLabel = styled('label')(({ theme }) => ({
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: theme.spacing(1),
}));

const ArtifactCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: theme.shadows[2]
  }
}));

// Main component
const SearchDialog = React.lazy(() => import('../components/common/SearchDialog'));

const Conversation = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const messageListRef = useRef(null);
  const [searchOpen, setSearchOpen] = React.useState(false);

  // Cmd+K keyboard shortcut for search
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const fileInputRef = useRef(null);
  const chatInputRef = useRef(null); // Ref for IsolatedChatInput to get/set value

  const {
    loading,
    error,
    conversation,
    isStreaming,
    streamingContent,
    getConversations,
    createConversation,
    getConversation,
    streamMessage,
    cancelStream,
    generateDocument,
    uploadFiles,
    setConversation,
    saveConversation
  } = useConversation();
  const { showNotification } = useNotification();
  const { getTaskAgent, getAgentAvatarUrl } = useAgentNames();

  // Direct mapping from task ID to agent name, role, and avatar for quick lookup
  const AGENT_TASK_MAP = {
    // Functional Analysis
    'project-info': {
      name: 'Proja',
      role: 'Project Info',
      avatar: '/static/media/avatars/face_co (1) 4.png',
      bgColor: '#e1f5fe'
    },
    'chat-prd': {
      name: 'Proja',
      role: 'Project Info',
      avatar: '/static/media/avatars/face_co (1) 4.png',
      bgColor: '#e1f5fe'
    },
    'extract-prd': {
      name: 'Proja',
      role: 'Project Info',
      avatar: '/static/media/avatars/face_co (1) 4.png',
      bgColor: '#e1f5fe'
    },
    'features': {
      name: 'Featy',
      role: 'Features',
      avatar: '/static/media/avatars/face_co (2) 4.png',
      bgColor: '#e8f5e9'
    },
    'user-roles': {
      name: 'Rolie',
      role: 'User Roles',
      avatar: '/static/media/avatars/face_co (3) 4.png',
      bgColor: '#fff8e1'
    },
    'user-stories': {
      name: 'Storia',
      role: 'User Stories',
      avatar: '/static/media/avatars/face_co (4) 4.png',
      bgColor: '#f3e5f5'
    },
    'process-flows': {
      name: 'Flo',
      role: 'Process Flows',
      avatar: '/static/media/avatars/face_co (5) 4.png',
      bgColor: '#fff3e0'
    },
    'flow': {
      name: 'Flo',
      role: 'Process Flows',
      avatar: '/static/media/avatars/face_co (5) 4.png',
      bgColor: '#fff3e0'
    },

    // Design
    'wireframes': {
      name: 'Wiro',
      role: 'UI/UX Wireframes',
      avatar: '/static/media/avatars/face_co (6) 4.png',
      bgColor: '#f1f8e9'
    },
    'design-system': {
      name: 'Stylia',
      role: 'UI/UX Style Guide',
      avatar: '/static/media/avatars/face_co (7) 4.png',
      bgColor: '#e0f2f1'
    },
    'design-guidelines': {
      name: 'Guy',
      role: 'UI/UX Guidelines',
      avatar: '/static/media/avatars/face_co (8) 4.png',
      bgColor: '#f9fbe7'
    },
    'designs': {
      name: 'Layo',
      role: 'UI/UX Layout',
      avatar: '/static/media/avatars/face_co (12) 4.png',
      bgColor: '#e8eaf6'
    },
    'design-references': {
      name: 'Refa',
      role: 'UI/UX References',
      avatar: '/static/media/avatars/face_co (13) 4.png',
      bgColor: '#fce4ec'
    },

    // Coding & Development
    'architecture': {
      name: 'Archie',
      role: 'Architecture',
      avatar: '/static/media/avatars/face_co (14) 4.png',
      bgColor: '#e3f2fd'
    },
    'api-guidelines': {
      name: 'Alex',
      role: 'API',
      avatar: '/static/media/avatars/face_co (15) 4.png',
      bgColor: '#ede7f6'
    },
    'technology-stack': {
      name: 'Stacky',
      role: 'Technology Stack',
      avatar: '/static/media/avatars/face_co (16) 4.png',
      bgColor: '#e0f7fa'
    },
    'tech-stack': {
      name: 'Stacky',
      role: 'Technology Stack',
      avatar: '/static/media/avatars/face_co (16) 4.png',
      bgColor: '#e0f7fa'
    },
    'dev-guidelines': {
      name: 'Cody',
      role: 'Coding Guidelines',
      avatar: '/static/media/avatars/face_co (17) 4.png',
      bgColor: '#f5f5f5'
    },
    'security-guidelines': {
      name: 'Secury',
      role: 'Security Guidelines',
      avatar: '/static/media/avatars/face_co (18) 4.png',
      bgColor: '#ffebee'
    },
    'user-permissions': {
      name: 'Missy',
      role: 'User Permissions',
      avatar: '/static/media/avatars/face_co (19) 4.png',
      bgColor: '#efebe9'
    },
    'field-validations': {
      name: 'Fieldy',
      role: 'Field Validations',
      avatar: '/static/media/avatars/face_co (20) 4.png',
      bgColor: '#eceff1'
    },
    'database': {
      name: 'Dora',
      role: 'Database',
      avatar: '/static/media/avatars/face_co (21) 4.png',
      bgColor: '#e8f5e9'
    },

    // Testing
    'test-cases': {
      name: 'Funcy',
      role: 'Functional Testing',
      avatar: '/static/media/avatars/face_co (23) 4.png',
      bgColor: '#fffde7'
    },
    'functional-testing': {
      name: 'Funcy',
      role: 'Functional Testing',
      avatar: '/static/media/avatars/face_co (23) 4.png',
      bgColor: '#fffde7'
    },
    'automated-testing': {
      name: 'Auton',
      role: 'Automated Testing',
      avatar: '/static/media/avatars/face_co (24) 4.png',
      bgColor: '#e1f5fe'
    },
    'security-testing': {
      name: 'Secu',
      role: 'Security Testing',
      avatar: '/static/media/avatars/face_co (25) 4.png',
      bgColor: '#fbe9e7'
    },
    'performance-testing': {
      name: 'Perfie',
      role: 'Performance Testing',
      avatar: '/static/media/avatars/face_co (26) 4.png',
      bgColor: '#f1f8e9'
    },
    'field-validation': {
      name: 'Fieldy',
      role: 'Field Validation',
      avatar: '/static/media/avatars/face_co (20) 4.png',
      bgColor: '#eceff1'
    },

    // Infrastructure
    'version-control': {
      name: 'Versio',
      role: 'Code Version Control',
      avatar: '/static/media/avatars/face_co (27) 5.png',
      bgColor: '#e8eaf6'
    },
    'cicd-pipeline': {
      name: 'Liny',
      role: 'CI/CD Pipeline',
      avatar: '/static/media/avatars/face_co (28) 4.png',
      bgColor: '#f3e5f5'
    },
    'deployment': {
      name: 'Deplo',
      role: 'Deployment',
      avatar: '/static/media/avatars/face_co (29) 4.png',
      bgColor: '#e0f2f1'
    },

    // Default
    'default': {
      name: 'Ailyce',
      role: 'AI Agent',
      avatar: '/static/media/avatars/face_co 4.png',
      bgColor: '#e3f2fd'
    }
  };

  // State
  const [project, setProject] = useState(null);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [conversationFiles, setConversationFiles] = useState([]);
  const [persistentFiles, setPersistentFiles] = useState([]); // Files that persist across the conversation
  const [showFilesPopup, setShowFilesPopup] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]); // Files uploaded but not yet sent with message
  const [uploadProgress, setUploadProgress] = useState({}); // Progress tracking for each file upload
  const [combinedUploadProgress, setCombinedUploadProgress] = useState({ total: 0, completed: 0, uploading: false, fileCount: 0 }); // Combined progress for all files
  const [generating, setGenerating] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [lastMessageId, setLastMessageId] = useState(null);

  // Artifact patch (human-in-the-loop)
  const [patchProposal, setPatchProposal] = useState(null); // { proposals, summary, pendingMessage }
  const [patchLoading, setPatchLoading] = useState(false);

  const [messagesFeedback, setMessagesFeedback] = useState({}); // Track feedback for each message
  const [visualPageEditKey, setVisualPageEditKey] = useState(0); // Key to trigger refresh when visual pages are edited
  const [lastEditedPage, setLastEditedPage] = useState(null); // Track the last edited page {pageId, taskType}

  // Handler for when visual pages (wireframes/designs/design-system) are edited
  const handleVisualPageEdited = (editInfo) => {
    console.log('Conversation: handleVisualPageEdited called with:', editInfo);
    // Track the edited page info
    if (editInfo?.pageId) {
      setLastEditedPage(editInfo);
    }
    setVisualPageEditKey(prev => {
      const newKey = prev + 1;
      console.log('Conversation: visualPageEditKey changing from', prev, 'to', newKey);
      return newKey;
    });
  };

  // Watch for wireframes/designs/design-system generation success messages to trigger refresh
  const lastMessageCountRef = useRef(0);
  useEffect(() => {
    if (!conversation?.messages) return;

    // Get task ID from conversation or URL
    const urlParams = new URLSearchParams(window.location.search);
    const currentTaskId = conversation?.taskId || conversation?.task_id || urlParams.get('task');
    if (!['wireframes', 'designs', 'design-system'].includes(currentTaskId)) return;

    const currentMessageCount = conversation.messages.length;
    // Only check if we have new messages
    if (currentMessageCount <= lastMessageCountRef.current) {
      lastMessageCountRef.current = currentMessageCount;
      return;
    }
    lastMessageCountRef.current = currentMessageCount;

    // Check if the latest assistant message indicates successful generation
    const latestMessages = conversation.messages.slice(-3); // Check last 3 messages
    const hasGenerationSuccess = latestMessages.some(msg =>
      msg.role === 'assistant' &&
      (msg.content?.includes('files generated') ||
       msg.content?.includes('created successfully') ||
       msg.content?.includes('Generated successfully') ||
       msg.content?.includes('update complete'))
    );

    if (hasGenerationSuccess) {
      console.log(`Conversation: Detected ${currentTaskId} generation success, triggering refresh`);
      setVisualPageEditKey(prev => prev + 1);
    }
  }, [conversation?.messages, conversation?.taskId, conversation?.task_id]);

  // Check if we should show the proceed button (for new features/change requests from create flow)
  const urlSearchParams = new URLSearchParams(window.location.search);
  const currentTask = urlSearchParams.get('task');
  const fromCreate = urlSearchParams.get('fromCreate');
  const showProceedButton = (currentTask === 'feature-info' || currentTask === 'change-request-info') && fromCreate === 'true';

  // Handler for proceed to PRD workflow
  const handleProceedToWorkflow = () => {
    navigate(`/projects/${projectId}/prd-workflow#foundation`);
  };

  // Load conversation files
  const loadConversationFiles = async () => {
    if (!conversation?._id) return;

    try {
      setLoadingFiles(true);
      const response = await fileUploadService.getFiles({
        conversationId: conversation._id,
        limit: 50
      });
      setConversationFiles(response.data || []);
    } catch (error) {
      console.error('Error loading conversation files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  // Load persistent files for the conversation
  const loadPersistentFiles = async () => {
    if (!conversation?._id) return;

    try {
      const files = await conversationService.getPersistentFiles(conversation._id);
      // Ensure we always set an array and filter out invalid entries
      const validFiles = Array.isArray(files) ? files.filter(file => file && typeof file === 'object' && file.file_id) : [];
      setPersistentFiles(validFiles);
    } catch (error) {
      console.error('Error loading persistent files:', error);
      setPersistentFiles([]); // Set empty array on error
    }
  };

  // Speech recognition states
  const [isListening, setIsListening] = useState(false);
  const [speechRecognition, setSpeechRecognition] = useState(null);

  // Load files when conversation changes
  useEffect(() => {
    if (conversation?._id) {
      loadConversationFiles();
      loadPersistentFiles();
    }
  }, [conversation?._id]);

  // Ensure persistentFiles is always an array
  useEffect(() => {
    if (!Array.isArray(persistentFiles)) {
      setPersistentFiles([]);
    }
  }, [persistentFiles]);

  // Close files popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilesPopup && !event.target.closest('[data-files-popup]')) {
        setShowFilesPopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilesPopup]);

  // Validation state
  const [isValidated, setIsValidated] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [interimTranscript, setInterimTranscript] = useState('');

  // Available languages for speech recognition
  const availableLanguages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'fr-FR', name: 'French' },
    { code: 'nl-NL', name: 'Dutch' },
    { code: 'de-DE', name: 'German' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'it-IT', name: 'Italian' }
  ];

  // Local state for streaming content
  const [localStreamingContent, setLocalStreamingContent] = useState('');
  const [localIsStreaming, setLocalIsStreaming] = useState(false);
  const [editingMessageIndex, setEditingMessageIndex] = useState(null);
  const [editingMessageContent, setEditingMessageContent] = useState('');

  // Refs for tracking state
  const streamingRef = useRef({
    isStreaming: false,
    content: '',
    lastUpdated: null,
    abortFunction: null
  });
  const speechRecognitionRef = useRef(null);
  const restartTimeoutRef = useRef(null);
  const speechTimeoutRef = useRef(null);
  const lastSpeechTimestampRef = useRef(Date.now());
  const debounceRef = useRef(null);
  const recognitionIdRef = useRef(0);
  const quickCreateProcessedRef = useRef(false);
  const confirmedTextRef = useRef(''); // Tracks confirmed (final) text during speech recognition
  const messageInputRef = useRef(null); // Ref for message input field

  // Function to scroll to bottom of message list
  const scrollToBottom = () => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  };

  // Force UI update timer for streaming content
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (streamingRef.current.isStreaming) {
        const now = Date.now();
        if (streamingRef.current.lastUpdated && (now - streamingRef.current.lastUpdated) > 100) {
          setLocalIsStreaming(true);
          setLocalStreamingContent(streamingRef.current.content);
        }
      }
    }, 200);

    return () => clearInterval(intervalId);
  }, []);

  // Initialize project and task-specific conversation
  useEffect(() => {
    let isComponentMounted = true;
    let initializedRef = false;

    const initProject = async () => {
      if (initializedRef) return;
      initializedRef = true;

      try {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const taskId = urlSearchParams.get('task');
        const docMode = urlSearchParams.get('doc_mode');
        const orgDocId = urlSearchParams.get('org_doc_id');

        const projectData = await projectService.getProjectById(projectId);
        if (!isComponentMounted) return;
        setProject(projectData);

        if (taskId) {
          // Optimized: Use get-or-create endpoint to avoid multiple API calls
          try {
            const taskTitle = getTaskTitle(taskId);
            const conversation = await conversationService.getOrCreateConversation(
              projectId,
              taskId,
              `${taskTitle} - ${new Date().toLocaleString()}`,
              docMode,
              orgDocId
            );

            if (conversation && isComponentMounted) {
              setConversation(conversation);

              // Handle pending field validation file upload after state is set
              if (window.__pendingFVFile && (conversation.id || conversation._id)) {
                const convId = conversation.id || conversation._id;
                const pendingFile = window.__pendingFVFile;
                delete window.__pendingFVFile;
                // Defer upload so conversation state is set first
                setTimeout(async () => {
                  try {
                    await conversationService.uploadFiles(convId, [pendingFile], '');
                    console.log('Pending FV file uploaded to conversation');
                  } catch (uploadErr) {
                    console.error('Error uploading pending FV file:', uploadErr);
                  }
                }, 500);
              }

              return;
            }
          } catch (err) {
            console.error(`Error getting or creating task-specific conversation for ${taskId}:`, err);
          }
        } else {
          // For general conversations (no task), still need to fetch the list to find the latest one
          let existingConversations = [];
          try {
            existingConversations = await getConversations(projectId);
          } catch (listErr) {
            console.error("Error listing conversations:", listErr);
          }

          if (existingConversations && existingConversations.length > 0) {
            try {
              const generalConversations = existingConversations.filter(
                conv => (!conv.taskId && !conv.task_id) || conv.taskId === 'chat-prd' || conv.task_id === 'chat-prd'
              );

              if (generalConversations.length > 0) {
                generalConversations.sort((a, b) =>
                  new Date(b.createdAt) - new Date(a.createdAt)
                );

                const latestConversation = await getConversation(generalConversations[0]._id);

                if (latestConversation && isComponentMounted) {
                  setConversation(latestConversation);
                  return;
                }
              } else if (existingConversations.length > 0) {
                const latestConversation = await getConversation(existingConversations[0]._id);

                if (latestConversation && isComponentMounted) {
                  setConversation(latestConversation);
                  return;
                }
              }
            } catch (convErr) {
              console.error("Error loading existing conversation:", convErr);
            }
          }
        }

        if (isComponentMounted) {
          const placeholderConversation = {
            _id: `temp-${Date.now()}`,
            title: `New Conversation - ${new Date().toLocaleString()}`,
            projectId: projectId,
            messages: [],
            artifacts: [],
            createdAt: new Date().toISOString()
          };

          setConversation(placeholderConversation);

          showNotification({
            message: "Ready to start a new conversation for this project",
            type: "info"
          });
        }
      } catch (err) {
        console.error("Error initializing project:", err);
        if (isComponentMounted) {
          showNotification({
            message: `Error: ${err.message || "Failed to load project"}`,
            type: 'error'
          });
        }
      }
    };

    // Helper function to get a human-readable title from task ID
    const getTaskTitle = (taskId) => {
      const taskTitles = {
        'project-info': 'Project Info',
        'features': 'Features',
        'user-roles': 'User Roles',
        'user-stories': 'User Stories',
        'process-flows': 'Process Flows',
        'design-references': 'UI/UX References',
        'wireframes': 'Wireframes',
        'design-guidelines': 'Design Guidelines',
        'designs': 'Designs',
        'design-system': 'UI/UX Style Guide',
        'knowledge-hub-design': 'Knowledge Hub UI/UX',
        'architecture': 'Architecture',
        'api-guidelines': 'API Guidelines',
        'tech-stack': 'Technology Stack',
        'field-validations': 'Field Validation',
        'dev-guidelines': 'Development Guidelines',
        'security-guidelines': 'Security Guidelines',
        'unit-testing': 'Unit Testing',
        'user-permissions': 'User Permissions',
        'knowledge-hub-coding': 'Knowledge Hub Coding',
        'functional-testing': 'Functional Testing',
        'automated-testing': 'Automated Testing',
        'security-testing': 'Security Testing',
        'performance-testing': 'Performance Testing',
        'knowledge-hub-testing': 'Knowledge Hub Quality',
        'git-guidelines': 'Code Version Control',
        'cicd-pipeline': 'CI/CD Pipeline',
        'deployment': 'Deployment',
        'knowledge-hub-infrastructure': 'Knowledge Hub Infrastructure'

      };

      return taskTitles[taskId] || taskId;
    };

    initProject();

    return () => {
      isComponentMounted = false;
    };
  }, [projectId, showNotification]);

  // Auto-scroll to bottom when messages change or during streaming
  useEffect(() => {
    scrollToBottom();
    setTimeout(scrollToBottom, 50);
    setTimeout(scrollToBottom, 200);
  }, [conversation?.messages, localStreamingContent]);

  // Cleanup when component unmounts or user navigates away
  useEffect(() => {
    // Listen for beforeunload to clean up when user navigates away
    const handleBeforeUnload = () => {
      const currentRecognition = speechRecognitionRef.current;
      if (currentRecognition) {
        try {
          currentRecognition._userStopped = true;
          currentRecognition.abort();
        } catch (e) { }
        try {
          currentRecognition.stop();
        } catch (e) { }
      }
      forceHideSpeechAnimation();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // Empty dependency array - only run on mount/unmount
  }, []);

  // Helper function to forcefully hide speech animation
  const forceHideSpeechAnimation = () => {
    console.log('Forcefully hiding speech animation');

    // Hide animation container
    const speechAnimation = document.querySelector('.speech-animation-container');
    if (speechAnimation) {
      speechAnimation.style.opacity = '0';
      speechAnimation.style.visibility = 'hidden';
    }

    // Hide wrapper
    const speechWrapper = document.querySelector('.speech-animation-wrapper');
    if (speechWrapper) {
      speechWrapper.style.display = 'none';
      speechWrapper.style.opacity = '0';
      speechWrapper.style.visibility = 'hidden';
    }

    // Reset mic buttons
    const micButtons = document.querySelectorAll('button[aria-label="Voice input"]');
    micButtons.forEach(button => {
      button.style.backgroundColor = 'transparent';
    });
  };

  // Force visual indicators for speech recognition
  useEffect(() => {
    // When isListening changes, force update the UI
    if (isListening) {
      console.log('Speech recognition active - forcing UI update');

      // Add a small delay to allow the DOM to update
      setTimeout(() => {
        // Show animation
        const speechAnimation = document.querySelector('.speech-animation-container');
        if (speechAnimation) {
          console.log('Forcing animation visibility');
          speechAnimation.style.opacity = '1';
          speechAnimation.style.visibility = 'visible';
          speechAnimation.style.boxShadow = '0 0 30px rgba(71, 89, 105, 0.5)';
        }

        // Show wrapper
        const speechWrapper = document.querySelector('.speech-animation-wrapper');
        if (speechWrapper) {
          speechWrapper.style.display = 'flex';
          speechWrapper.style.opacity = '1';
          speechWrapper.style.visibility = 'visible';
        }

        // Make mic buttons more visible
        const micButtons = document.querySelectorAll('button[aria-label="Stop listening"]');
        micButtons.forEach(button => {
          button.style.backgroundColor = 'rgba(71, 89, 105, 0.5)';
        });
      }, 100);
    } else {
      // When speech recognition stops, hide the animation
      console.log('Speech recognition stopped - hiding UI elements');
      forceHideSpeechAnimation();
    }
  }, [isListening]);

  // Initialize speech recognition
  useEffect(() => {
    setLocalStreamingContent('');
    setLocalIsStreaming(false);

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;
        recognition.lang = selectedLanguage;

        speechRecognitionRef.current = recognition;
      } catch (error) {
        console.error('Failed to initialize speech recognition:', error);
      }
    }

    return () => {
      setLocalStreamingContent('');
      setLocalIsStreaming(false);

      if (speechRecognitionRef.current) {
        try {
          speechRecognitionRef.current._userStopped = true;

          try {
            if (typeof speechRecognitionRef.current.abort === 'function') {
              speechRecognitionRef.current.abort();
            } else {
              speechRecognitionRef.current.stop();
            }
          } catch (stopError) {
            console.error('First cleanup stop attempt failed:', stopError);
            try {
              if (typeof speechRecognitionRef.current.stop === 'function') {
                speechRecognitionRef.current.stop();
              }
            } catch (error) {
              console.error('All cleanup stop attempts failed:', error);
            }
          }
        } catch (error) {
          console.error('Error stopping speech recognition during cleanup:', error);
        }
      }

      setIsListening(false);
      setInterimTranscript('');

      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }

      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  // Check for quick create description
  useEffect(() => {
    if (quickCreateProcessedRef.current) return;
    if (!conversation) return;

    quickCreateProcessedRef.current = true;

    const quickCreateDescription = localStorage.getItem('quickCreateDescription');
    const quickCreateProjectId = localStorage.getItem('quickCreateProjectId');

    if (quickCreateDescription && quickCreateProjectId === projectId) {
      const isDuplicate = conversation.messages?.some(msg =>
        msg.role === 'user' && areMessagesSimilar(msg.content, quickCreateDescription)
      );

      if (isDuplicate) {
        localStorage.removeItem('quickCreateDescription');
        localStorage.removeItem('quickCreateProjectId');
        return;
      }

      if (!conversation._id.startsWith('temp-')) {
        // Handle quick create with new streamMessage API
        const streamResult = streamMessage(conversation._id, quickCreateDescription);
        // Store abort function if needed
        if (streamResult && streamResult.abort) {
          streamingRef.current.abortFunction = streamResult.abort;
        }

        localStorage.removeItem('quickCreateDescription');
        localStorage.removeItem('quickCreateProjectId');
      } else {
        setMessage(quickCreateDescription);
      }
    }
  }, [conversation]);

  // Function to toggle speech recognition
  const toggleListening = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (isListening) {
      const currentRecognition = speechRecognitionRef.current;

      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }

      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }

      if (currentRecognition) {
        try {
          console.log('Stopping speech recognition');
          currentRecognition._userStopped = true;

          // Try both abort and stop methods for better browser compatibility
          try {
            if (typeof currentRecognition.abort === 'function') {
              currentRecognition.abort();
              console.log('Recognition aborted');
            }
          } catch (e) {
            console.error('Error aborting speech recognition:', e);
          }

          try {
            if (typeof currentRecognition.stop === 'function') {
              currentRecognition.stop();
              console.log('Recognition stopped');
            }
          } catch (e) {
            console.error('Error stopping speech recognition:', e);
          }
        } catch (error) {
          console.error('Error stopping speech recognition:', error);
        }
      }

      // Make sure to update the UI to reflect the stopped state
      setIsListening(false);
      setInterimTranscript('');
      // Reset confirmed text ref when stopping
      confirmedTextRef.current = '';

      // Focus the chat input field after stopping so user can type or press Enter to send
      if (chatInputRef.current?.focus) {
        chatInputRef.current.focus();
      }

      // Force any hidden animations to disappear
      setTimeout(() => {
        const speechAnimation = document.querySelector('.speech-animation-container');
        if (speechAnimation) {
          speechAnimation.style.opacity = '0';
          speechAnimation.style.visibility = 'hidden';
        }
      }, 50);
    } else {
      const recognition = speechRecognitionRef.current;

      if (!recognition) {
        showNotification({
          message: "Speech recognition is not available in your browser.",
          type: "error"
        });
        return;
      }

      // Directly call startSpeechRecognition which will use getUserMedia
      // to trigger the browser's permission dialog if needed
      startSpeechRecognition();
    }
  };

  // Helper function to start speech recognition
  const startSpeechRecognition = async () => {
    const recognition = speechRecognitionRef.current;
    if (!recognition) return;

    try {
      // First try to get actual microphone access - this will trigger the browser's permission dialog
      try {
        console.log('Requesting microphone access via getUserMedia...');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stop the stream immediately as we just needed to check/request permission
        stream.getTracks().forEach(track => track.stop());
        console.log('Microphone access granted via getUserMedia');
      } catch (error) {
        console.error('getUserMedia failed:', error);
        // Check the specific error type for better messaging
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          showNotification({
            message: "Microphone access was denied. Please allow microphone access in your browser settings and try again.",
            type: "error"
          });
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          showNotification({
            message: "No microphone was found. Please ensure a microphone is connected and try again.",
            type: "error"
          });
        } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
          showNotification({
            message: "Could not access your microphone. It may be in use by another application.",
            type: "error"
          });
        } else {
          showNotification({
            message: "Could not access microphone. Please check your browser settings and try again.",
            type: "error"
          });
        }
        return;
      }

      const currentRecognitionId = ++recognitionIdRef.current;

      // Clear previous transcript and start fresh for new voice input session
      confirmedTextRef.current = '';
      setMessage('');
      setInterimTranscript('');
      // Also clear the IsolatedChatInput
      if (chatInputRef.current?.clear) {
        chatInputRef.current.clear();
      }

      recognition._handlersSet = true;
      recognition._userStopped = false;
      recognition._failed = false;
      recognition._id = currentRecognitionId;

      recognition.lang = selectedLanguage;

      recognition.onresult = (event) => {
        try {
          let interimTranscript = '';
          let finalTranscript = '';

          lastSpeechTimestampRef.current = Date.now();

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;

            if (event.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }

          // When we get final transcript, add it to the confirmed text
          if (finalTranscript) {
            const lowerFinalTranscript = finalTranscript.toLowerCase().trim();
            const sendTriggers = ['send', 'submit', 'send message', 'submit message', 'send it', 'go ahead'];

            if (sendTriggers.some(trigger => lowerFinalTranscript.includes(trigger))) {
              const contentBeforeSend = finalTranscript.split(new RegExp(sendTriggers.join('|'), 'i'))[0].trim();
              if (contentBeforeSend) {
                confirmedTextRef.current += contentBeforeSend + ' ';
                setMessage(confirmedTextRef.current);
              }

              setTimeout(() => {
                handleMessageSend();
              }, 300);
            } else {
              // Add final transcript to confirmed text
              confirmedTextRef.current += finalTranscript + ' ';
              setMessage(confirmedTextRef.current);
            }
            // Clear interim since we got final
            setInterimTranscript('');
          }

          // Show interim transcript in real-time in the input field
          if (interimTranscript !== '') {
            setInterimTranscript(interimTranscript);
            // Show confirmed text + interim text (interim in lighter style would need UI changes)
            setMessage(confirmedTextRef.current + interimTranscript);
          }
        } catch (resultError) {
          console.error('Error processing speech recognition results:', resultError);
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error, event);

        // Check if this is during initial startup
        const isStartupError = Date.now() - (recognition._startTime || 0) < 1000;

        if (event.error === 'not-allowed') {
          // Only show error if this is during startup, not a delayed permission issue
          if (isStartupError) {
            showNotification({
              message: "Microphone access was denied. Please allow microphone access to use speech input.",
              type: "error"
            });
            recognition._failed = true;
            setIsListening(false);
            // Force immediate UI update
            forceHideSpeechAnimation();
          }
        } else if (event.error === 'audio-capture') {
          showNotification({
            message: "No microphone was found. Ensure that a microphone is installed and configured correctly.",
            type: "error"
          });
          recognition._failed = true;
          setIsListening(false);
          // Force immediate UI update
          forceHideSpeechAnimation();
        } else if (event.error === 'network') {
          recognition._failed = false;
        } else if (event.error === 'aborted' || event.error === 'no-speech') {
          recognition._failed = false;
        } else {
          console.warn(`Speech recognition encountered error: ${event.error}`);
          if (event.error === 'service-not-allowed' || event.error === 'bad-grammar') {
            recognition._failed = true;
          }
        }
      };

      recognition.onstart = () => {
        lastSpeechTimestampRef.current = Date.now();
        monitorSpeechActivity();
      };

      recognition.onend = () => {
        if (!recognition._userStopped && isListening) {
          restartTimeoutRef.current = setTimeout(() => {
            if (isListening) {
              try {
                recognition.start();
              } catch (error) {
                console.error('Failed to restart speech recognition:', error);
                setIsListening(false);
                forceHideSpeechAnimation();
              }
            }
          }, 300);
        } else {
          setIsListening(false);
          setInterimTranscript('');
        }
      };

      // Don't set isListening to true until we successfully start recognition
      setTimeout(() => {
        try {
          console.log('Starting speech recognition...', recognition);
          recognition._startTime = Date.now(); // Track when we started for error handling
          recognition.start();

          // Only set listening state after successful start
          setIsListening(true);

          // Focus the chat input field so Enter key sends the message
          if (chatInputRef.current?.focus) {
            chatInputRef.current.focus();
          }

          // Force the central animation to be visible
          setTimeout(() => {
            const speechAnimation = document.querySelector('.speech-animation-container');
            if (speechAnimation) {
              console.log('Found speech animation, forcing visibility');
              speechAnimation.style.opacity = '1';
              speechAnimation.style.visibility = 'visible';
            } else {
              console.log('Speech animation not found in DOM');
            }

            if (isListening) {
              showNotification({
                message: `Listening in ${availableLanguages.find(lang => lang.code === selectedLanguage)?.name || 'selected language'}...`,
                type: "info"
              });
            }
          }, 500);
        } catch (error) {
          console.error('Failed to start speech recognition:', error);
          setIsListening(false);
          forceHideSpeechAnimation();

          // Handle specific error messages
          if (error.message && (error.message.includes('not-allowed') || error.message.includes('NotAllowedError'))) {
            showNotification({
              message: "Microphone access was denied. Please allow microphone access in your browser settings and try again.",
              type: "error"
            });
          } else {
            showNotification({
              message: "Could not start voice input. Please try again.",
              type: "error"
            });
          }
        }
      }, 50);
    } catch (error) {
      console.error('Error in speech recognition setup:', error);
      setIsListening(false);
      forceHideSpeechAnimation();
      showNotification({
        message: "Speech recognition setup failed. Please try again or use a different browser.",
        type: "error"
      });
    }
  };

  // Monitor for periods of silence and restart if necessary
  const monitorSpeechActivity = () => {
    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
    }

    speechTimeoutRef.current = setTimeout(() => {
      if (isListening) {
        const timeSinceLastSpeech = Date.now() - lastSpeechTimestampRef.current;

        if (timeSinceLastSpeech > 10000) {
          if (speechRecognition) {
            try {
              speechRecognition.stop();
              setTimeout(() => {
                if (isListening) {
                  speechRecognition.start();
                  lastSpeechTimestampRef.current = Date.now();
                }
              }, 300);
            } catch (error) {
              console.error('Error restarting after silence:', error);
            }
          }
        }

        monitorSpeechActivity();
      }
    }, 5000);
  };

  // ── Artifact Patch: Propose → Confirm → Apply ──

  const handleProposePatch = async (e, inputValue = null) => {
    e?.preventDefault();
    const messageContent = (inputValue || chatInputRef.current?.getValue() || message || '').trim();
    const convId = conversation?._id || conversation?.id;
    if (!messageContent || !convId) return;

    setPatchLoading(true);
    try {
      const result = await conversationService.proposePatch(convId, messageContent);

      if (result.success && result.proposals?.length > 0) {
        // Show confirmation dialog with proposed changes
        setPatchProposal({
          proposals: result.proposals,
          summary: result.summary,
          pendingMessage: messageContent,
        });
      } else if (result.is_question) {
        // Not a change request — send as normal chat
        showNotification({ message: result.summary || 'This looks like a question. Sending as chat...', type: 'info' });
        handleMessageSend(e, messageContent);
      } else {
        // No proposals generated — fall back to chat
        showNotification({ message: result.error || result.summary || 'Could not generate patches. Sending as chat...', type: 'info' });
        handleMessageSend(e, messageContent);
      }
    } catch (err) {
      console.error('Propose patch failed:', err);
      showNotification({ message: `Patch proposal failed: ${err}. Sending as chat...`, type: 'warning' });
      handleMessageSend(e, messageContent);
    } finally {
      setPatchLoading(false);
    }
  };

  const handleApplyPatch = async () => {
    const convId = conversation?._id || conversation?.id;
    if (!patchProposal || !convId) return;

    setPatchLoading(true);
    try {
      const result = await conversationService.applyPatch(
        convId,
        patchProposal.proposals,
        patchProposal.summary
      );

      if (result.success) {
        showNotification({ message: `Applied: ${patchProposal.summary}`, type: 'success' });
        // Add user message to conversation
        setConversation(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: [
              ...(prev.messages || []),
              { role: 'user', content: patchProposal.pendingMessage, createdAt: new Date().toISOString() },
              { role: 'assistant', content: `✅ ${patchProposal.summary}`, createdAt: new Date().toISOString() },
            ]
          };
        });
        // Clear input
        if (chatInputRef.current?.setValue) chatInputRef.current.setValue('');
        setMessage('');
        // Refresh conversation to get updated artifact
        getConversation(convId).catch(console.error);
      } else {
        showNotification({ message: `Apply failed: ${result.error}`, type: 'error' });
      }
    } catch (err) {
      console.error('Apply patch failed:', err);
      showNotification({ message: `Apply failed: ${err}`, type: 'error' });
    } finally {
      setPatchLoading(false);
      setPatchProposal(null);
    }
  };

  const handleRejectPatch = () => {
    setPatchProposal(null);
    // User can still send as normal chat if they want
  };

  // Handle message sending
  // inputValue parameter is passed from IsolatedChatInput, falls back to message state
  const handleMessageSend = async (e, inputValue = null) => {
    e?.preventDefault();
    // Use inputValue from IsolatedChatInput if provided, otherwise fall back to state
    const currentMessage = inputValue !== null ? inputValue : message;
    if ((!currentMessage.trim() && files.length === 0 && uploadedFiles.length === 0) || localIsStreaming) return;

    // Make sure speech recognition is stopped when sending a message
    if (isListening) {
      console.log('Stopping speech recognition before sending message');
      toggleListening();
    }

    // Make sure animation is hidden using our helper
    forceHideSpeechAnimation();

    try {
      const messageContent = currentMessage.trim();

      if (files.length === 0 && conversation && conversation.messages && Array.isArray(conversation.messages)) {
        const lastMessages = conversation.messages.slice(-5).filter(msg => msg.role === 'user');
        const isDuplicate = lastMessages.some(msg =>
          areMessagesSimilar(msg.content, messageContent)
        );

        if (isDuplicate) {
          showNotification({
            message: "Duplicate message detected. Message not sent.",
            type: "info"
          });
          // Clear both IsolatedChatInput and state
          if (chatInputRef.current) chatInputRef.current.clear();
          setMessage('');
          setFiles([]);
          return;
        }
      }

      // Note: files from the old dialog system are no longer used
      // uploadedFiles from immediate upload will be included automatically

      setLocalStreamingContent('');
      setLocalIsStreaming(true);

      setIsTyping(true);

      // Clear both IsolatedChatInput and state
      if (chatInputRef.current) chatInputRef.current.clear();
      setMessage('');
      setFiles([]);
      setUploadedFiles([]); // Clear uploaded files when message is sent

      let activeConversation;

      try {
        if (!conversation || conversation._id.startsWith('temp-')) {
          const newConversation = await createConversation(
            projectId,
            `Conversation - ${new Date().toLocaleString()}`
          );

          if (!newConversation || !newConversation._id) {
            throw new Error("Failed to create new conversation");
          }

          activeConversation = newConversation;
          setConversation(newConversation);
        } else {
          activeConversation = conversation;
        }

        setIsTyping(false);

        // Prepare file attachments from uploaded files
        const fileAttachments = uploadedFiles.map(file => ({
          file_id: file.id,
          claude_file_id: file.claude_file_id,
          filename: file.fileName || file.filename,
          content_type: file.content_type || file.mime_type,
          purpose: file.purpose || 'content'
        }));

        const userMessage = {
          role: 'user',
          content: messageContent,
          createdAt: new Date().toISOString(),
          attachments: fileAttachments.length > 0 ? fileAttachments : undefined,
          isLocal: true, // Mark as locally added to help with deduplication
          tempId: `temp-${Date.now()}-${Math.random()}`
        };

        if (activeConversation && activeConversation.messages) {
          setConversation(prev => ({
            ...prev,
            messages: [...(prev.messages || []), userMessage]
          }));
        }

        // Get the stream result which contains abort function and promise
        const streamResult = conversationService.streamMessage(
          activeConversation._id,
          messageContent,
          (chunk, fullText) => {
            // Strip internal tool markers from displayed content
            const displayText = fullText.replace(/\n__TOOLS_USED__\n/g, '');

            streamingRef.current = {
              ...streamingRef.current,
              isStreaming: true,
              content: displayText,
              lastUpdated: Date.now()
            };

            setLocalIsStreaming(true);
            setLocalStreamingContent(displayText);
          },
          (finalText) => {
            streamingRef.current = {
              isStreaming: false,
              content: '',
              lastUpdated: Date.now(),
              abortFunction: null
            };

            setLocalStreamingContent('');
            setLocalIsStreaming(false);

            // Check if tools were used (marker injected by conversationService)
            // Add message locally for instant display
            const cleanText = (finalText || '').replace(/\n__TOOLS_USED__\n/g, '');
            if (cleanText.trim()) {
              const assistantMessage = {
                role: 'assistant',
                content: cleanText,
                createdAt: new Date().toISOString()
              };

              setTimeout(() => {
                setConversation(prev => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    messages: [...(prev.messages || []), assistantMessage]
                  };
                });
              }, 10);
            }

            {
              // Always refresh from server after streaming — picks up tool changes + split view data

              setTimeout(() => {
                if (!localIsStreaming) {
                  getConversation(activeConversation._id).catch(err => {
                    console.error("Failed to refresh conversation after streaming:", err);
                  });
                }
              }, 1000);
            }
          },
          (error) => {
            console.error("Streaming error:", error);
            setLocalIsStreaming(false);
            showNotification({
              message: `Error: ${error.message || "Failed to stream message"}`,
              type: 'error'
            });
          },
          fileAttachments
        );

        // Store the abort function immediately - it's available right away
        if (streamResult && streamResult.abort) {
          console.log('Storing abort function for immediate use');
          streamingRef.current.abortFunction = streamResult.abort;
        }

        // Wait for the streaming to complete
        await streamResult.promise;

      } catch (err) {
        console.error("Failed in conversation handling:", err);
        setIsTyping(false);
        setLocalIsStreaming(false);

        showNotification({
          message: "Failed to process message: " + (err.message || "Unknown error"),
          type: "error"
        });
        return;
      }
    } catch (err) {
      setIsTyping(false);
      setLocalIsStreaming(false);
      console.error("Error sending message:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to send message"}`,
        type: 'error'
      });
    }
  };

  // Handle file selection - Claude.ai style immediate upload with multiple files
  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    // Check file size limits (configurable from environment)
    const maxSizeMB = parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 100;
    const maxSize = maxSizeMB * 1024 * 1024; // Convert MB to bytes
    const validFiles = [];
    const invalidFiles = [];

    selectedFiles.forEach(file => {
      if (file.size > maxSize) {
        invalidFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    // Show error for invalid files
    if (invalidFiles.length > 0) {
      showNotification({
        message: `Files exceed ${maxSizeMB}MB limit: ${invalidFiles.join(', ')}`,
        type: "warning"
      });
    }

    // Upload valid files immediately
    if (validFiles.length > 0) {
      await handleImmediateFileUpload(validFiles);
    }

    // Reset file input for next selection
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Immediate file upload without dialog with progress tracking
  const handleImmediateFileUpload = async (filesToUpload) => {
    try {
      const conversationId = conversation?._id;
      if (!conversationId) {
        throw new Error('No conversation available for file upload');
      }

      const totalFiles = filesToUpload.length;

      // Initialize combined progress tracking
      setCombinedUploadProgress({
        total: totalFiles * 100, // Total progress units (100% per file)
        completed: 0,
        uploading: true,
        fileCount: totalFiles,
        completedFiles: 0
      });

      // Initialize individual file progress tracking (internal use only)
      const fileProgressTracker = {};
      filesToUpload.forEach((file, index) => {
        fileProgressTracker[`${file.name}_${index}`] = {
          fileName: file.name,
          progress: 0,
          status: 'uploading'
        };
      });
      setUploadProgress(fileProgressTracker);

      // Upload files individually with progress tracking
      const uploadPromises = filesToUpload.map(async (file, index) => {
        const fileKey = `${file.name}_${index}`;

        try {
          const result = await fileUploadService.uploadFile(file, {
            conversationId: conversationId,
            description: `File uploaded to conversation`,
            purpose: 'content',
            onProgress: (percentCompleted) => {
              // Update individual file progress
              setUploadProgress(prev => {
                const updated = {
                  ...prev,
                  [fileKey]: {
                    ...prev[fileKey],
                    progress: percentCompleted
                  }
                };

                // Calculate combined progress
                const totalProgress = Object.values(updated).reduce((sum, file) => sum + (file.progress || 0), 0);
                setCombinedUploadProgress(prevCombined => ({
                  ...prevCombined,
                  completed: totalProgress
                }));

                return updated;
              });
            }
          });

          // Mark as completed
          setUploadProgress(prev => {
            const updated = {
              ...prev,
              [fileKey]: {
                ...prev[fileKey],
                progress: 100,
                status: 'completed'
              }
            };

            // Update completed files count
            const completedCount = Object.values(updated).filter(f => f.status === 'completed').length;
            setCombinedUploadProgress(prevCombined => ({
              ...prevCombined,
              completedFiles: completedCount,
              completed: Object.values(updated).reduce((sum, file) => sum + (file.progress || 0), 0)
            }));

            return updated;
          });

          return {
            ...result,
            fileName: file.name,
            fileSize: file.size,
            uploadedAt: new Date().toISOString()
          };
        } catch (error) {
          // Mark as failed
          setUploadProgress(prev => ({
            ...prev,
            [fileKey]: {
              ...prev[fileKey],
              status: 'failed',
              error: error.message
            }
          }));
          console.error(`Failed to upload ${file.name}:`, error);
          throw error;
        }
      });

      const uploadResults = await Promise.all(uploadPromises);

      // Add to uploaded files state (these will be shown above input)
      setUploadedFiles(prev => [...prev, ...uploadResults]);

      // Add uploaded files to persistent files list for carry-forward
      for (const uploadedFile of uploadResults) {
        try {
          // Validate uploadedFile has required properties
          if (!uploadedFile || !uploadedFile.id) {
            console.warn('Invalid uploaded file, skipping persistent file addition:', uploadedFile);
            continue;
          }

          const persistentFileData = {
            type: 'file',
            file_id: uploadedFile.id,
            file_name: uploadedFile.fileName || uploadedFile.filename || 'Unknown file',
            file_url: `/api/files/${uploadedFile.id}/download`,
            content_type: uploadedFile.content_type || uploadedFile.mime_type || 'application/octet-stream',
            claude_file_id: uploadedFile.claude_file_id || null
          };

          await conversationService.addPersistentFile(conversation._id, persistentFileData);
        } catch (error) {
          console.error('Error adding file to persistent list:', error);
          // Continue with other files even if one fails
        }
      }

      // Refresh conversation files list and persistent files
      loadConversationFiles();
      loadPersistentFiles();

      // Clear progress tracking after a delay
      setTimeout(() => {
        setUploadProgress({});
        setCombinedUploadProgress({ total: 0, completed: 0, uploading: false, fileCount: 0, completedFiles: 0 });
      }, 2000);

      // Show success notification
      showNotification({
        message: `✅ Successfully uploaded ${uploadResults.length} file(s)`,
        type: 'success',
        autoHideDuration: 3000
      });

    } catch (error) {
      console.error('File upload error:', error);
      showNotification({
        message: `❌ Upload failed: ${error.message}`,
        type: 'error',
        autoHideDuration: 5000
      });

      // Clear progress tracking on error
      setTimeout(() => {
        setUploadProgress({});
        setCombinedUploadProgress({ total: 0, completed: 0, uploading: false, fileCount: 0, completedFiles: 0 });
      }, 3000);
    }
  };

  // Ultra simplified file upload handler
  const handleFileUpload = async () => {
    try {
      if (files.length === 0) return;

      // Check file size before attempting upload
      const file = files[0];
      const MAX_SIZE = 5 * 1024 * 1024; // 5MB

      if (file.size > MAX_SIZE) {
        setFilesDialogOpen(false);
        showNotification({
          message: `❌ File too large: ${file.name} exceeds the ${Math.round(MAX_SIZE / 1024 / 1024)}MB limit`,
          type: 'error',
          autoHideDuration: 5000
        });
        return;
      }

      // Close dialog first to avoid issues
      setFilesDialogOpen(false);

      // Create an upload notification
      showNotification({
        message: '📤 Uploading file... Please wait',
        type: 'info',
        autoHideDuration: 5000
      });

      let currentConvId = conversation ? conversation._id : null;

      // Create a new conversation if needed
      if (!currentConvId || currentConvId.startsWith('temp-')) {
        try {
          const newConversation = await createConversation(
            projectId,
            `File Upload - ${new Date().toLocaleString()}`
          );

          currentConvId = newConversation._id;
          setConversation(newConversation);
        } catch (err) {
          throw new Error("Failed to create conversation: " + (err.message || "Unknown error"));
        }
      }

      // Perform the actual upload with better error capture
      try {
        const uploadResult = await uploadFiles(currentConvId, files, message);

        // If message was empty, keep track of uploaded files for next message
        if (!message.trim()) {
          setUploadedFiles(prev => [...prev, ...files.map(f => ({
            file: f,
            uploadedAt: new Date(),
            conversationId: currentConvId
          }))]);
        }

        // Reset current file selection state
        setFiles([]);
        setMessage('');

        // Refresh the conversation files list and persistent files
        loadConversationFiles();
        loadPersistentFiles();

        // Refresh the conversation
        setTimeout(async () => {
          try {
            await getConversation(currentConvId);

            showNotification({
              message: `✅ File "${file.name}" uploaded successfully!`,
              type: 'success',
              autoHideDuration: 3000
            });
          } catch (refreshErr) {
            console.error("Error refreshing conversation:", refreshErr);
          }
        }, 1000);
      } catch (uploadErr) {
        console.error("Upload operation failed:", uploadErr);
        throw uploadErr; // Re-throw to be caught by outer handler
      }
    } catch (err) {
      console.error("Error handling file upload:", err);

      // Detailed error message with troubleshooting tip
      const errorMsg = err.message || "Unknown error";
      const baseErrorMessage = `❌ Upload failed: ${errorMsg}`;

      // Add specific troubleshooting advice based on error type
      let fullErrorMessage = baseErrorMessage;

      if (errorMsg.includes("unexpected end") || errorMsg.includes("end of form")) {
        fullErrorMessage = `${baseErrorMessage}\n\nTry with a smaller file or a different file format.`;
      } else if (errorMsg.includes("network") || errorMsg.includes("timeout")) {
        fullErrorMessage = `${baseErrorMessage}\n\nCheck your internet connection and try again.`;
      }

      showNotification({
        message: fullErrorMessage,
        type: 'error',
        autoHideDuration: 8000 // Longer duration for error messages
      });

      // If we still have files in state, allow user to try again
      if (files.length > 0) {
        setTimeout(() => {
          setFilesDialogOpen(true);
        }, 1000);
      }
    }
  };

  // Handle document generation
  const handleGenerateDocument = async () => {
    try {
      if (!conversation || conversation._id.startsWith('temp-')) {
        showNotification({
          message: "Send a message first to create a conversation before generating a PRD",
          type: "info"
        });
        return;
      }

      setGenerating(true);

      const document = await generateDocument(conversation._id, conversation.taskId || conversation.task_id || 'project-info');

      showNotification({
        message: 'PRD generated successfully!',
        type: 'success'
      });

      const shouldProceed = window.confirm('PRD generated successfully! Would you like to continue to the PRD workflow to create development artifacts?');

      if (shouldProceed) {
        navigate(`/projects/${projectId}/prd-workflow/${document._id}`);
      } else {
        navigate(`/projects/${projectId}/documents/${document._id}`);
      }
    } catch (err) {
      console.error("Error generating PRD:", err);
      showNotification({
        message: `Error: ${err.message}`,
        type: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  // Handle markdown file import
  const handleMarkdownImport = async (importData) => {
    try {
      const { content, file, fileName, taskType } = importData;

      if (!conversation?._id || !projectId) {
        throw new Error('No active conversation found');
      }

      // Upload the MD file to the server so it appears in Project Uploaded Documents
      if (file) {
        try {
          await fileUploadService.uploadFile(file, {
            projectId,
            conversationId: conversation._id,
            description: `Imported MD file: ${fileName}`,
            purpose: 'content'
          });
        } catch (uploadErr) {
          console.error("Error uploading imported MD file:", uploadErr);
          // Continue with import even if upload fails
        }
      }

      // Add imported content directly as an assistant message
      // This will automatically trigger split view parsing and display
      await conversationService.addMessage(
        conversation._id,
        content, // Raw markdown content for split view parsing
        'assistant', // Assistant role triggers split view
        [] // No attachments
      );

      // Refresh the conversation to show the imported content and trigger split view
      await getConversation(conversation._id);

      showNotification({
        message: `Successfully imported ${fileName} - content appears in the right panel`,
        type: 'success'
      });

    } catch (err) {
      console.error("Error importing markdown file:", err);
      showNotification({
        message: `Error importing file: ${err.message}`,
        type: 'error'
      });
    }
  };

  // Map task IDs to human-readable titles
  const taskTitles = {
    'chat-prd': 'Project Info',
    'project-info': 'Project Info',
    'extract-prd': 'PRD Extraction',
    'field-validations': 'Field Validation',
    'tech-stack': 'Technology Stack',
    'dev-guidelines': 'Development Guidelines',
    'design-guidelines': 'Design Guidelines',
    'knowledge-hub-analysis': 'Knowledge Hub Analysis',
    'wireframes': 'Wireframes',
    'designs': 'Designs',
    'design-system': 'Design System',
    'security-guidelines': 'Security Guidelines',
    'knowledge-hub-design': 'Knowledge Hub UI/UX',
    'api-guidelines': 'API Guidelines',
    'features': 'Features',
    'user-stories': 'User Stories',
    'architecture': 'Architecture',
    'process-flows': 'Process Flows',
    'flow': 'Process Flows',
    'user-roles': 'User Roles',
    'unit-testing': 'Unit Testing',
    'user-permissions': 'User Permissions',
    'knowledge-hub-coding': 'Knowledge Hub Coding',
    'functional-testing': 'Functional Testing',
    'automated-testing': 'Automated Testing',
    'security-testing': 'Security Testing',
    'performance-testing': 'Performance Testing',
    'knowledge-hub-testing': 'Knowledge Hub Quality',
    'git-guidelines': 'Git Guidelines',
    'cicd-pipeline': 'CI/CD Pipeline',
    'aws-deployment': 'AWS Deployment',
    'knowledge-hub-infrastructure': 'Knowledge Hub Infrastructure',
  };

  // State for section avatar if task-specific
  const [sectionAvatar, setSectionAvatar] = useState({
    url: null,
    name: null,
    sectionId: null
  });

  // Map task IDs directly to their section IDs
  const taskToSectionMap = {
    'project-info': 'features',  // Assuming Project Info belongs to Features section
    'chat-prd': 'features',
    'extract-prd': 'features',
    'features': 'features',
    'user-stories': 'user-stories',
    'user-roles': 'user-roles',
    'wireframes': 'design',
    'designs': 'design',
    'design-system': 'design',
    'field-validations': 'features',
    'tech-stack': 'architecture',
    'dev-guidelines': 'development',
    'api-guidelines': 'api',
    'security-guidelines': 'security',
    'architecture': 'architecture',
    'process-flows': 'architecture',
    'flow': 'architecture',
    'unit-testing': 'quality', // Map unit-testing to quality section
    'user-permissions': 'security', // Map user-permissions to security section
    'functional-testing': 'quality', // Map functional-testing to quality section
    'automated-testing': 'quality', // Map automated-testing to quality section
    'security-testing': 'security', // Map security-testing to security section
    'performance-testing': 'quality', // Map performance-testing to quality section
    'git-guidelines': 'development', // Map git-guidelines to development section
    'cicd-pipeline': 'development', // Map cicd-pipeline to development section
    'aws-deployment': 'development' // Map aws-deployment to development section
  };

  // Fetch section avatar if we have a task ID
  useEffect(() => {
    const fetchSectionAvatar = async () => {
      try {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const taskId = urlSearchParams.get('task');

        if (taskId) {
          console.log("Fetching avatar for task ID:", taskId);

          // Get the section ID directly from the task ID mapping
          const sectionId = taskToSectionMap[taskId];

          if (sectionId) {
            console.log("Mapped section ID for task:", sectionId);

            // Fetch the section avatar
            const sectionResponse = await fetch(`/api/sections/${sectionId}/avatar`);
            if (!sectionResponse.ok) {
              throw new Error(`Failed to fetch section avatar: ${sectionResponse.status}`);
            }

            const sectionData = await sectionResponse.json();
            console.log("Section avatar response:", sectionData);

            if (sectionData.success) {
              setSectionAvatar({
                url: sectionData.avatarUrl,
                name: sectionData.avatarName || taskTitles[taskId], // Use task title as fallback name
                sectionId: sectionId
              });
              console.log("Set section avatar for conversation:", sectionData);
            }
          } else {
            console.log("No section mapping found for task:", taskId);
          }
        }
      } catch (error) {
        console.error("Error fetching section avatar:", error);
      }
    };

    fetchSectionAvatar();
  }, [projectId]);

  // Get assistant display name from direct task ID map, or fallbacks
  const getAssistantName = () => {
    // Get task ID from either field name format
    const taskId = conversation?.taskId || conversation?.task_id;

    // First priority: direct lookup from task ID to agent name
    if (taskId && AGENT_TASK_MAP[taskId]) {
      return AGENT_TASK_MAP[taskId].name;
    }

    // Second fallback: use agentNames util
    if (taskId) {
      const taskAgent = getTaskAgent({ id: taskId });
      if (taskAgent && taskAgent.name) {
        return taskAgent.name;
      }
    }

    // Third priority: section avatar if available (for task-specific conversations)
    if (sectionAvatar?.name) {
      return sectionAvatar.name;
    }

    // Fourth priority: project avatar if available
    if (project?.avatar?.name) {
      return project.avatar.name;
    }

    // Final fallback: project name or default
    return project?.name ? `${project.name} Assistant` : 'Assistant';
  };

  // Get avatar photo URL if available - direct map task agent first, then other fallbacks
  const getAvatarUrl = () => {
    // First priority: get avatar directly from our mapping
    if (conversation?.taskId && AGENT_TASK_MAP[conversation.taskId]) {
      const agentInfo = AGENT_TASK_MAP[conversation.taskId];
      if (agentInfo.avatar) {
        // For React applications, relative URLs in src attributes are evaluated relative to the domain root
        // This makes the path look like: http://localhost:3000/static/media/avatars/filename.png
        return agentInfo.avatar;
      }
    }

    // Second fallback: try using the agent utility
    if (conversation?.taskId) {
      const agentName = getTaskAgent({ id: conversation.taskId })?.name;
      if (agentName) {
        const agentAvatarUrl = getAgentAvatarUrl(agentName);
        if (agentAvatarUrl) {
          return agentAvatarUrl;
        }
      }
    }

    // Third priority: section avatar URL
    if (sectionAvatar?.url) {
      return sectionAvatar.url.startsWith('/') ?
        `${window.location.origin}${sectionAvatar.url}` :
        sectionAvatar.url;
    }

    // Fourth priority: project avatar URL
    return project?.avatar?.photoUrl || null;
  };

  // Get background color for avatar from mapping
  const getAvatarBgColor = () => {
    if (conversation?.taskId && AGENT_TASK_MAP[conversation.taskId]) {
      return AGENT_TASK_MAP[conversation.taskId].bgColor || '#e3f2fd';
    }
    return '#e3f2fd'; // Default light blue
  };

  // Get user name from localStorage or use a default
  const getUserName = () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user'));
      if (storedUser && storedUser.name) {
        return storedUser.name.split(' ')[0];
      }
    } catch (e) {
      console.error("Error getting user name:", e);
    }
    return "there";
  };

  // Get current task name based on URL parameters
  const getCurrentTaskName = () => {
    try {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const taskId = urlSearchParams.get('task');

      if (taskId) {
        // First try to get role from direct mapping
        if (AGENT_TASK_MAP[taskId]) {
          return AGENT_TASK_MAP[taskId].role;
        }
        // Fall back to task titles map
        return taskTitles[taskId] || taskId;
      }
    } catch (e) {
      console.error("Error getting task name:", e);
    }
    return "PRD";
  };

  // Function to check if content is mostly HTML code
  const isHtmlContent = (content) => {
    // Look for HTML indicators: doctype, html tags, or significant HTML structure
    return content.includes('<!DOCTYPE html>') ||
      content.includes('<html') ||
      content.includes('</html>') ||
      (content.includes('<body') && content.includes('</body>'));
  };

  // Function to detect ping/heartbeat messages during generation
  const isPingOrHeartbeatContent = (content) => {
    if (!content) return false;
    // Only match actual ping/heartbeat patterns — NOT real streamed content
    const hasPingPattern = content.includes(': ping -') ||
                           content.includes('ping - 2025') ||
                           content.includes('ping - 2024') ||
                           content.includes('ping - 2026');
    // Only treat as heartbeat if content is VERY short and repetitive
    // (real streamed content from SDK updates will be longer and meaningful)
    const trimmed = content.trim();
    const isOnlyAssistantMarkers = trimmed.length < 50 &&
                                    (content.match(/assistant/gi) || []).length >= 3 &&
                                    trimmed.replace(/assistant/gi, '').trim().length < 10;
    return hasPingPattern || isOnlyAssistantMarkers;
  };

  // Function to clean HTML message content
  const cleanHtmlContent = (content, taskId) => {
    // Check for ping/heartbeat messages during wireframe/design generation
    if ((taskId === 'wireframes' || taskId === 'designs' || taskId === 'design-system') && isPingOrHeartbeatContent(content)) {
      if (taskId === 'wireframes') {
        return "🎨 Generating wireframes for your project. Please wait, this may take a few minutes...";
      } else if (taskId === 'design-system') {
        return "🎨 Creating design system for your project. Please wait, this may take a few minutes...";
      } else if (taskId === 'designs') {
        return "🎨 Creating designs for your project. Please wait, this may take a few minutes...";
      }
    }

    // If this is a wireframe, design or design system task, and content contains HTML
    if ((taskId === 'wireframes' || taskId === 'designs' || taskId === 'design-system') && isHtmlContent(content)) {
      // Create a cleaner HTML-free message
      if (taskId === 'wireframes') {
        return "I've generated wireframes for your project. You can view them in the preview panel on the left.";
      } else if (taskId === 'design-system') {
        return "I've created a design system for your project. You can view it in the preview panel on the left.";
      } else if (taskId === 'designs') {
        return "I've created designs for your project. You can view them in the preview panel on the left.";
      }
    }
    // Otherwise return the original content
    return content;
  };

  // Render chat messages
  const renderMessages = () => {
    let messages = [];

    if (conversation) {
      if (conversation.messages && Array.isArray(conversation.messages)) {
        // Create a deep copy and clean HTML content for specific task types
        messages = conversation.messages.map(msg => {
          if (msg.role === 'assistant') {
            return {
              ...msg,
              content: cleanHtmlContent(msg.content, conversation.taskId)
            };
          }
          return { ...msg };
        });
      }
    }

    if (isTyping) {
      messages.push({
        role: 'assistant',
        content: '...',
        isTyping: true
      });
    }

    if (localIsStreaming || streamingRef.current.isStreaming) {
      const rawContent = streamingRef.current.isStreaming ?
        streamingRef.current.content :
        localStreamingContent;

      // Clean streaming content if needed
      const cleanedContent = cleanHtmlContent(rawContent || '...', conversation?.taskId);

      messages.push({
        role: 'assistant',
        content: cleanedContent,
        isStreaming: true,
        createdAt: new Date().toISOString()
      });
    }

    return messages.map((msg, index) => {
      if (msg.role === 'system') return null;

      const avatarUrl = getAvatarUrl();
      const assistantName = getAssistantName();

      return (
        <ChatMessageContainer key={index} sender={msg.role}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            mb: 1,
            width: '100%',
            alignSelf: 'flex-start'
          }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 0.5,
                justifyContent: 'flex-start'
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  mr: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: msg.role === 'user' ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                  overflow: 'hidden'
                }}
              >
                {msg.role === 'assistant' ? (
                  <AvatarWithFallback
                    name={assistantName}
                    src={avatarUrl}
                    bgColor={getAvatarBgColor()}
                    size={28}
                    fontSize={14}
                  />
                ) : (
                  // User avatar
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      fontSize: '0.8rem'
                    }}
                  >
                    Y
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
              >
                {msg.role === 'user' ? (msg.createdByName || msg.created_by_name || getUserName()) : assistantName}
              </Typography>
            </Box>
          </Box>
          <Box sx={{
            width: '100%'
          }}>
            <ChatBubble
              sender={msg.role}
              isStreaming={msg.isStreaming}>
              {msg.isTyping ? (
                <Box display="flex" alignItems="center" p={1}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {assistantName} is thinking...
                  </Typography>
                </Box>
              ) : (
                <>
                  {msg.role === 'assistant' ? (
                    <Box
                      sx={{
                        position: 'relative',
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          fontWeight: 600,
                          marginTop: 2,
                          marginBottom: 1,
                        },
                        '& h1': { fontSize: '1.5rem' },
                        '& h2': { fontSize: '1.3rem' },
                        '& h3': { fontSize: '1.1rem' },
                        '& p': { marginBottom: 1.5 },
                        '& ul, & ol': { paddingLeft: 3, marginBottom: 1.5 },
                        '& li': { marginBottom: 0.5 },
                        '& ol ol, & ul ul, & ol ul, & ul ol': {
                          marginBottom: 0
                        },
                        '& li > ol, & li > ul': {
                          marginTop: 0.5
                        },
                        '& ol': {
                          counterReset: 'item',
                          listStyleType: 'none'
                        },
                        '& ol > li': {
                          counterIncrement: 'item',
                          position: 'relative',
                          paddingLeft: '0.5em'
                        },
                        '& ol > li::before': {
                          content: 'counters(item, ".") ". "',
                          position: 'absolute',
                          left: '-1.5em',
                          width: '1.5em',
                          textAlign: 'right'
                        },
                        '& ol ol > li::before': {
                          content: 'counters(item, ".") " "'
                        },
                        '& code': {
                          backgroundColor: '#f5f5f5',
                          padding: '2px 4px',
                          borderRadius: 4,
                          fontSize: '0.9em',
                        },
                        '& pre': {
                          backgroundColor: '#f5f5f5',
                          padding: 1.5,
                          borderRadius: 4,
                          overflowX: 'auto',
                          marginBottom: 1.5,
                        },
                        '& blockquote': {
                          borderLeft: '3px solid #ccc',
                          paddingLeft: 1.5,
                          marginLeft: 0,
                          marginBottom: 1.5,
                          fontStyle: 'italic',
                        }
                      }}
                    >
                      {(() => {
                        const segments = parseToolActivity(msg.content);
                        return segments.map((seg, segIdx) =>
                          seg.type === 'tool' ? (
                            <ToolActivityBlock key={segIdx} tools={seg.tools} />
                          ) : (
                            <MarkdownRenderer key={segIdx} content={seg.content} />
                          )
                        );
                      })()}
                    </Box>
                  ) : (
                    <Box>
                      {editingMessageIndex === index ? (
                        // Edit mode - only show text field in bubble
                        <TextField
                          fullWidth
                          multiline
                          value={editingMessageContent}
                          onChange={(e) => setEditingMessageContent(e.target.value)}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              // Save the edited message
                              if (editingMessageContent.trim()) {
                                // Update the message in conversation
                                const updatedMessages = [...messages];
                                updatedMessages[index] = {
                                  ...updatedMessages[index],
                                  content: editingMessageContent.trim()
                                };

                                // Remove all messages after the edited message
                                const messagesToKeep = updatedMessages.slice(0, index + 1);

                                // Update conversation state
                                setConversation(prev => ({
                                  ...prev,
                                  messages: messagesToKeep
                                }));

                                // Clear edit state
                                setEditingMessageIndex(null);
                                setEditingMessageContent('');

                                // Save and regenerate response
                                try {
                                  // Save the conversation with all messages
                                  await saveConversation({
                                    ...conversation,
                                    messages: messagesToKeep
                                  });

                                  setLocalStreamingContent('');
                                  setLocalIsStreaming(true);
                                  setIsTyping(true);

                                  // Get the stream result which contains abort function and promise
                                  const streamResult = conversationService.streamMessage(
                                    conversation._id,
                                    editingMessageContent.trim(),
                                    (chunk, fullText) => {
                                      streamingRef.current = {
                                        ...streamingRef.current,
                                        isStreaming: true,
                                        content: fullText,
                                        lastUpdated: Date.now()
                                      };

                                      setLocalIsStreaming(true);
                                      setLocalStreamingContent(fullText);
                                    },
                                    (finalText) => {
                                      const assistantMessage = {
                                        role: 'assistant',
                                        content: finalText,
                                        createdAt: new Date().toISOString()
                                      };

                                      streamingRef.current = {
                                        isStreaming: false,
                                        content: '',
                                        lastUpdated: Date.now(),
                                        abortFunction: null
                                      };

                                      setLocalStreamingContent('');
                                      setLocalIsStreaming(false);

                                      setTimeout(() => {
                                        setConversation(prev => {
                                          if (!prev) return prev;
                                          return {
                                            ...prev,
                                            messages: [...(prev.messages || []), assistantMessage]
                                          };
                                        });
                                      }, 10);

                                      setTimeout(() => {
                                        if (!localIsStreaming) {
                                          getConversation(conversation._id).catch(err => {
                                            console.error("Failed to refresh conversation after streaming:", err);
                                          });
                                        }
                                      }, 500);
                                    }
                                  );

                                  // Store abort function for potential cancellation
                                  if (streamResult && streamResult.abort) {
                                    streamingRef.current.abortFunction = streamResult.abort;
                                  }

                                  setIsTyping(false);
                                } catch (error) {
                                  console.error('Failed to save conversation:', error);
                                  showNotification({
                                    message: 'Failed to save edited message. Please try again.',
                                    type: 'error'
                                  });
                                  // Restore edit state on error
                                  setEditingMessageIndex(index);
                                  setEditingMessageContent(editingMessageContent);
                                }
                              }
                            } else if (e.key === 'Escape') {
                              // Cancel editing
                              setEditingMessageIndex(null);
                              setEditingMessageContent('');
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              padding: '5px 12px !important',
                              fontSize: '16px',
                              '& fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.1)',
                              },
                              '&:hover fieldset': {
                                borderColor: '#485869',
                              },
                              '&.Mui-focused fieldset': {
                                borderWidth: '1px',
                                borderColor: '#485869',
                              }
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '16px',
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        // Display mode
                        <Box
                          sx={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: 'rgba(0, 0, 0, 0.05)',
                              borderRadius: '10px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: 'rgba(0, 0, 0, 0.2)',
                              borderRadius: '10px',
                              '&:hover': {
                                background: 'rgba(0, 0, 0, 0.3)',
                              },
                            },
                            '& p': {
                              marginBottom: 1,
                              '&:last-child': { marginBottom: 0 }
                            },
                            '& ul, & ol': {
                              paddingLeft: 2.5,
                              marginBottom: 1,
                              marginTop: 0.5,
                              listStyle: 'none'
                            },
                            '& li': {
                              marginBottom: 0.25,
                              position: 'relative',
                              paddingLeft: '1.2em'
                            },
                            '& ul > li::before': {
                              content: '"•"',
                              position: 'absolute',
                              left: 0,
                              color: 'text.secondary'
                            },
                            '& ol': {
                              counterReset: 'item',
                              listStyleType: 'none'
                            },
                            '& ol > li': {
                              counterIncrement: 'item',
                              position: 'relative',
                              paddingLeft: '1.5em'
                            },
                            '& ol > li::before': {
                              content: 'counter(item) ". "',
                              position: 'absolute',
                              left: 0,
                              color: 'text.secondary'
                            },
                            '& > *:first-child': {
                              marginTop: 0
                            },
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          {(() => {
                            const segments = parseToolActivity(msg.content);
                            return segments.map((seg, segIdx) =>
                              seg.type === 'tool' ? (
                                <ToolActivityBlock key={segIdx} tools={seg.tools} />
                              ) : (
                                <MarkdownRenderer key={segIdx} content={seg.content} />
                              )
                            );
                          })()}
                        </Box>
                      )}
                    </Box>
                  )}

                  {msg.attachments && msg.attachments.length > 0 && (
                    <Box mt={2} mb={1} sx={{ borderTop: '1px solid rgba(0,0,0,0.1)', pt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                        {msg.attachments.length === 1 ? 'Attached file:' : `Attached files (${msg.attachments.length}):`}
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        alignItems: 'center'
                      }}>
                        {msg.attachments.map((attachment, i) => {
                          const isImage = attachment.fileType?.startsWith('image/');
                          const fileUrl = attachment.url || `/api/files/${attachment.fileId}`;

                          return (
                            <Box
                              key={i}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                border: '1px solid rgba(0,0,0,0.1)',
                                borderRadius: 1,
                                overflow: 'hidden',
                                width: isImage ? 120 : 100,
                                height: isImage ? 120 : 100,
                                p: isImage ? 0 : 1,
                                bgcolor: isImage ? 'transparent' : 'rgba(0,0,0,0.03)',
                                position: 'relative',
                                '&:hover': {
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  '& .file-overlay': {
                                    display: 'flex'
                                  }
                                }
                              }}
                            >
                              {isImage ? (
                                <Box
                                  component="img"
                                  src={fileUrl}
                                  alt={attachment.fileName}
                                  sx={{
                                    objectFit: 'cover',
                                    width: '100%',
                                    height: '100%'
                                  }}
                                />
                              ) : (
                                <>
                                  <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main', mt: 1 }} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      textAlign: 'center',
                                      mt: 0.5,
                                      width: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      px: 1
                                    }}
                                  >
                                    {attachment.fileName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: '0.65rem',
                                      mt: 'auto',
                                      mb: 0.5
                                    }}
                                  >
                                    {attachment.fileType?.split('/')[1] || 'document'}
                                  </Typography>
                                </>
                              )}

                              <Box
                                className="file-overlay"
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  bgcolor: 'rgba(0,0,0,0.5)',
                                  display: 'none',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <Tooltip title="View file">
                                  <IconButton
                                    size="small"
                                    sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.3)' }}
                                    onClick={() => window.open(fileUrl, '_blank')}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {(msg.isStreaming || (localIsStreaming && msg === messages[messages.length - 1])) && (
                    <Typography
                      variant="body2"
                      color="primary.main"
                      sx={{ mt: 1, display: 'flex', alignItems: 'center' }}
                    >
                      <span style={{ color: '#2196f3', marginRight: '6px' }}>●</span>
                      {getAssistantName()} is generating... ({msg.content.length} chars)
                    </Typography>
                  )}

                  {msg.role === 'assistant' && !msg.isTyping && !msg.isStreaming && !msg.isWelcome && (
                    <Box sx={{
                      display: 'flex',
                      mt: 1.5,
                      justifyContent: 'flex-end',
                      borderTop: '1px solid rgba(0,0,0,0.05)',
                      pt: 1
                    }}>
                      <Tooltip title="Copy message">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(msg.content);
                            showNotification({
                              message: "Message copied to clipboard",
                              type: "success"
                            });
                          }}
                          sx={{ mr: 1 }}
                        >
                          <MenuBookIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Helpful">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setMessagesFeedback({
                              ...messagesFeedback,
                              [index]: 'helpful'
                            });
                            showNotification({
                              message: "Thanks for your feedback!",
                              type: "success"
                            });
                          }}
                          sx={{
                            mr: 1,
                            color: messagesFeedback[index] === 'helpful' ? 'success.main' : 'inherit',
                            '&:hover': {
                              color: messagesFeedback[index] === 'helpful' ? 'success.dark' : 'inherit'
                            }
                          }}
                        >
                          <ThumbUpIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Not helpful">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setMessagesFeedback({
                              ...messagesFeedback,
                              [index]: 'not-helpful'
                            });
                            setLastMessageId(index);
                            setFeedbackDialogOpen(true);
                          }}
                          sx={{
                            mr: 1,
                            color: messagesFeedback[index] === 'not-helpful' ? 'error.main' : 'inherit',
                            '&:hover': {
                              color: messagesFeedback[index] === 'not-helpful' ? 'error.dark' : 'inherit'
                            }
                          }}
                        >
                          <ThumbDownIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Regenerate response">
                        <IconButton
                          size="small"
                          onClick={async () => {
                            // Find the previous user message
                            let userMessage = null;
                            let userMessageIndex = -1;
                            for (let i = index - 1; i >= 0; i--) {
                              if (messages[i].role === 'user') {
                                userMessage = messages[i].content;
                                userMessageIndex = i;
                                break;
                              }
                            }

                            if (userMessage && !localIsStreaming) {
                              try {
                                // Remove all messages after the user message we're regenerating from
                                const messagesToKeep = messages.slice(0, userMessageIndex + 1);

                                // Update local state
                                setConversation(prev => ({
                                  ...prev,
                                  messages: messagesToKeep
                                }));

                                // Save the updated conversation to backend
                                await saveConversation(conversation._id, {
                                  messages: messagesToKeep,
                                  projectId: conversation.projectId || conversation.project_id,
                                  title: conversation.title
                                });

                                // Now regenerate the response
                                setLocalStreamingContent('');
                                setLocalIsStreaming(true);
                                setIsTyping(true);

                                // Get the stream result
                                const streamResult = conversationService.streamMessage(
                                  conversation._id,
                                  userMessage,
                                  (chunk, fullText) => {
                                    streamingRef.current = {
                                      ...streamingRef.current,
                                      isStreaming: true,
                                      content: fullText,
                                      lastUpdated: Date.now()
                                    };

                                    setLocalIsStreaming(true);
                                    setLocalStreamingContent(fullText);
                                  },
                                  (finalText) => {
                                    const assistantMessage = {
                                      role: 'assistant',
                                      content: finalText,
                                      createdAt: new Date().toISOString()
                                    };

                                    streamingRef.current = {
                                      isStreaming: false,
                                      content: '',
                                      lastUpdated: Date.now(),
                                      abortFunction: null
                                    };

                                    setLocalStreamingContent('');
                                    setLocalIsStreaming(false);

                                    setTimeout(() => {
                                      setConversation(prev => {
                                        if (!prev) return prev;
                                        return {
                                          ...prev,
                                          messages: [...(prev.messages || []), assistantMessage]
                                        };
                                      });
                                    }, 10);

                                    setTimeout(() => {
                                      if (!localIsStreaming) {
                                        getConversation(conversation._id).catch(err => {
                                          console.error("Failed to refresh conversation after streaming:", err);
                                        });
                                      }
                                    }, 500);
                                  }
                                );

                                // Store abort function for potential cancellation
                                if (streamResult && streamResult.abort) {
                                  streamingRef.current.abortFunction = streamResult.abort;
                                }

                                setIsTyping(false);

                                showNotification({
                                  message: "Regenerating response...",
                                  type: "info"
                                });
                              } catch (error) {
                                console.error('Failed to regenerate response:', error);
                                showNotification({
                                  message: 'Failed to regenerate response. Please try again.',
                                  type: 'error'
                                });
                                setLocalIsStreaming(false);
                                setIsTyping(false);
                              }
                            }
                          }}
                          disabled={localIsStreaming}
                          sx={{
                            borderRadius: '4px',
                            '&:hover': {
                              color: 'primary.main',
                              borderRadius: '50%',
                              backgroundColor: 'rgba(25, 118, 210, 0.05)'
                            },
                            '&.Mui-disabled': {
                              opacity: 0.5
                            }
                          }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </>
              )}
            </ChatBubble>
            {/* Action buttons for user messages - outside the bubble */}
            {msg.role === 'user' && !localIsStreaming && !streamingRef.current.isStreaming && (() => {
              // Find the last user message index
              let lastUserMessageIndex = -1;
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].role === 'user') {
                  lastUserMessageIndex = i;
                  break;
                }
              }
              // Only show edit button for the last user message
              return index === lastUserMessageIndex;
            })() && (
                <Box sx={{
                  mt: 0.5,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                  opacity: editingMessageIndex === index ? 1 : 0.6,
                  '&:hover': { opacity: 1 },
                  transition: 'opacity 0.2s',
                  maxWidth: "97.4% !important"
                }}>
                  {editingMessageIndex === index ? (
                    // Edit mode buttons
                    <>
                      <Tooltip title="Save & Regenerate">
                        <IconButton
                          size="small"
                          onClick={async () => {
                            if (editingMessageContent.trim()) {
                              // Update the message in conversation
                              const updatedMessages = [...messages];
                              updatedMessages[index] = {
                                ...updatedMessages[index],
                                content: editingMessageContent.trim()
                              };

                              // Remove all messages after the edited message
                              const messagesToKeep = updatedMessages.slice(0, index + 1);

                              // Update local state
                              setConversation(prev => ({
                                ...prev,
                                messages: messagesToKeep
                              }));

                              // Clear edit state
                              setEditingMessageIndex(null);
                              setEditingMessageContent('');

                              // Save the updated conversation to backend
                              try {
                                // Save the conversation with all messages
                                await saveConversation(conversation._id, {
                                  messages: messagesToKeep,
                                  projectId: conversation.projectId || conversation.project_id,
                                  title: conversation.title
                                });

                                // After successful save, regenerate the response
                                setLocalStreamingContent('');
                                setLocalIsStreaming(true);
                                setIsTyping(true);

                                // Get the stream result which contains abort function and promise
                                const streamResult = conversationService.streamMessage(
                                  conversation._id,
                                  editingMessageContent.trim(),
                                  (chunk, fullText) => {
                                    streamingRef.current = {
                                      ...streamingRef.current,
                                      isStreaming: true,
                                      content: fullText,
                                      lastUpdated: Date.now()
                                    };

                                    setLocalIsStreaming(true);
                                    setLocalStreamingContent(fullText);
                                  },
                                  (finalText) => {
                                    const assistantMessage = {
                                      role: 'assistant',
                                      content: finalText,
                                      createdAt: new Date().toISOString()
                                    };

                                    streamingRef.current = {
                                      isStreaming: false,
                                      content: '',
                                      lastUpdated: Date.now(),
                                      abortFunction: null
                                    };

                                    setLocalStreamingContent('');
                                    setLocalIsStreaming(false);

                                    setTimeout(() => {
                                      setConversation(prev => {
                                        if (!prev) return prev;
                                        return {
                                          ...prev,
                                          messages: [...(prev.messages || []), assistantMessage]
                                        };
                                      });
                                    }, 10);

                                    setTimeout(() => {
                                      if (!localIsStreaming) {
                                        getConversation(conversation._id).catch(err => {
                                          console.error("Failed to refresh conversation after streaming:", err);
                                        });
                                      }
                                    }, 500);
                                  }
                                );

                                // Store abort function for potential cancellation
                                if (streamResult && streamResult.abort) {
                                  streamingRef.current.abortFunction = streamResult.abort;
                                }

                                setIsTyping(false);
                              } catch (error) {
                                console.error('Failed to save conversation:', error);
                                showNotification({
                                  message: 'Failed to save edited message. Please try again.',
                                  type: 'error'
                                });
                                // Restore edit state on error
                                setEditingMessageIndex(index);
                                setEditingMessageContent(editingMessageContent);
                              }
                            }
                          }}
                          sx={{
                            p: 0.5,
                            color: '#475969',
                            marginRight: '0.5rem !important',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.5)',
                              color: 'success.main'
                            }
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingMessageIndex(null);
                            setEditingMessageContent('');
                          }}
                          sx={{
                            p: 0.5,
                            color: '#475969',
                            marginRight: '0.5rem !important',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.5)',
                              color: 'error.main'
                            }
                          }}
                        >
                          <CancelIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    // View mode - edit button
                    <Tooltip title="Edit message">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingMessageIndex(index);
                          setEditingMessageContent(msg.content);
                        }}
                        sx={{
                          p: 0.5,
                          color: '#475969',
                          marginRight: '0.5rem !important',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.5)',
                            color: 'primary.main'
                          }
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
          </Box>
        </ChatMessageContainer>
      );
    });
  };

  // Render artifacts
  const renderArtifacts = () => {
    if (!conversation || !conversation.artifacts || conversation.artifacts.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography color="text.secondary">
            No artifacts saved yet. Artifacts are key pieces of information extracted from your conversation.
          </Typography>
        </Box>
      );
    }

    return conversation.artifacts.map((artifact, index) => (
      <ArtifactCard key={index}>
        <CardHeader
          title={artifact.title}
          subheader={`${artifact.type} • ${formatDateTime(artifact.createdAt)}`}
          action={
            <IconButton size="small">
              <StarBorderIcon />
            </IconButton>
          }
        />
        <CardContent>
          <Typography variant="body2">{artifact.content}</Typography>
        </CardContent>
      </ArtifactCard>
    ));
  };

  // Loading state
  if (loading && !conversation) {
    return (
      <ConversationLayout project={project}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress />
        </Box>
      </ConversationLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <ConversationLayout project={project}>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
            <Typography variant="body2" sx={{ mt: 1 }}>
              There may be an issue with connecting to the conversation API. The system will attempt to create a new conversation.
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createConversation(projectId, `New Conversation ${formatDateTime(new Date())}`)
                  .then(() => {
                    showNotification({
                      message: 'Created a new conversation successfully',
                      type: 'success'
                    });
                  })
                  .catch(err => {
                    showNotification({
                      message: `Failed to create conversation: ${err.message}`,
                      type: 'error'
                    });
                  });
              }}
            >
              Create New Conversation
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(`/projects/${projectId}`)}
            >
              Back to Project
            </Button>
          </Box>
        </Box>
      </ConversationLayout>
    );
  }

  // Handler for creating a new conversation
  const handleNewConversation = async () => {
    try {
      showNotification({
        message: 'Creating new conversation...',
        type: 'info'
      });

      const urlSearchParams = new URLSearchParams(window.location.search);
      const taskId = urlSearchParams.get('task');

      const taskTitle = taskId ? (taskTitles[taskId] || taskId) : null;
      const conversationTitle = taskId
        ? `${taskTitle} - ${new Date().toLocaleString()}`
        : `New Conversation - ${new Date().toLocaleString()}`;

      const newConversation = await createConversation(
        projectId,
        conversationTitle,
        taskId
      );

      if (newConversation && newConversation._id) {
        // Update local state
        setConversation(newConversation);

        // Reset UI state
        setFiles([]);
        setMessage('');

        // Navigate to the new conversation URL with proper project context
        let newUrl = `/projects/${projectId}/conversation`;
        if (taskId) {
          newUrl += `?task=${taskId}`;
        }
        navigate(newUrl, { replace: true });

        showNotification({
          message: 'Created new conversation successfully',
          type: 'success'
        });

        // Force refresh conversations list when returning to project
        localStorage.setItem('refreshConversations', 'true');
      } else {
        throw new Error("Failed to create new conversation");
      }
    } catch (err) {
      console.error("Error creating new conversation:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to create new conversation"}`,
        type: 'error'
      });
    }
  };

  // Handler for saving the document and marking as validated
  const handleSaveDocument = async () => {
    if (!conversation || !conversation._id) {
      showNotification({
        message: 'No active conversation to save',
        type: 'error'
      });
      return;
    }

    try {
      setGenerating(true);

      // Get the task ID from URL or conversation
      const urlSearchParams = new URLSearchParams(window.location.search);
      const taskId = urlSearchParams.get('task') || conversation.taskId;

      // Save validation status for the conversation
      await taskValidatedItemService.saveValidatedItem({
        projectId,
        taskId,
        conversationId: conversation._id,
        validated_id: conversation._id // Using conversation ID as the validated ID
      });

      // Generate the document
      const document = await generateDocument(conversation._id, taskId || 'project-info');
      showNotification({
        message: 'Content marked as Ready! Document saved successfully!',
        type: 'success'
      });

      if (document && document._id) {
        navigate(`/projects/${projectId}/documents/${document._id}`);
      }
    } catch (error) {
      console.error('Error saving document:', error);
      showNotification({
        message: `Failed to mark as ready: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  const renderChatInput = () => (
    <form onSubmit={handleMessageSend} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
            {/* Upload Progress Display - Single Combined Progress Bar */}
      {combinedUploadProgress.uploading && combinedUploadProgress.fileCount > 0 && (
        <Box sx={{
          mb: 2,
          p: 2,
          border: '1px solid rgba(25, 118, 210, 0.3)',
          borderRadius: 2,
          backgroundColor: 'rgba(25, 118, 210, 0.05)',
          width: '100%',
          maxWidth: 500
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
              Uploading {combinedUploadProgress.fileCount} file{combinedUploadProgress.fileCount > 1 ? 's' : ''}...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {Math.round((combinedUploadProgress.completed / combinedUploadProgress.total) * 100)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={combinedUploadProgress.total > 0 ? (combinedUploadProgress.completed / combinedUploadProgress.total) * 100 : 0}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main',
                borderRadius: 4
              }
            }}
          />
          {combinedUploadProgress.completedFiles > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {combinedUploadProgress.completedFiles} of {combinedUploadProgress.fileCount} file{combinedUploadProgress.fileCount > 1 ? 's' : ''} completed
            </Typography>
          )}
        </Box>
      )}

      {/* Persistent Files Area - Files that carry forward in conversation */}
      {Array.isArray(persistentFiles) && persistentFiles.length > 0 && (
        <Box sx={{
          mb: 2,
          p: 2,
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: 2,
          backgroundColor: 'rgba(76, 175, 80, 0.05)',
          width: '100%'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
            📎 {persistentFiles.length} file{persistentFiles.length > 1 ? 's' : ''} {persistentFiles.length > 1 ? 'have' : 'has'}  been uploaded
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {persistentFiles.filter(file => file && typeof file === 'object').map((file, index) => {
              // Safety check to ensure file object exists and has required properties
              if (!file || !file.file_id) return null;

              return (
                <Box
                  key={`persistent-${file.file_id || index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.3)',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    fontSize: '0.875rem'
                  }}
                >
                  <Typography variant="body2" sx={{ mr: 1, fontSize: '0.75rem' }}>
                    {file.file_name || 'Unknown file'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      try {
                        await conversationService.removePersistentFile(conversation._id, file.file_id);
                        loadPersistentFiles(); // Refresh the list
                      } catch (error) {
                        console.error('Error removing persistent file:', error);
                      }
                    }}
                    sx={{
                      p: 0.25,
                      color: 'rgba(76, 175, 80, 0.7)',
                      '&:hover': {
                        color: 'error.main',
                        backgroundColor: 'rgba(244, 67, 54, 0.1)'
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Audio, Language, and File buttons moved to top and aligned left */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        width: '100%',
        mb: 1,
        gap: 1
      }}>
        <Tooltip title={isListening ? "Stop listening" : "Voice input"}>
          <IconButton
            size="small"
            onClick={toggleListening}
            color={isListening ? "default" : "default"}
            sx={{
              animation: isListening ? 'pulse 1.5s infinite' : 'none',
              borderRadius: '8px',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                '100%': { transform: 'scale(1)', opacity: 1 }
              },
              bgcolor: isListening ? 'rgba(71, 89, 105, 0.4)' : 'transparent',
              '&:hover': {
                bgcolor: isListening ? 'rgba(71, 89, 105, 0.5)' : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {isListening ? (
              <>
                {/* Animated square ripple for active state */}
                <Box sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                  animation: 'rippleEffect 2s infinite',
                  '@keyframes rippleEffect': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(71, 89, 105, 0.4)',
                      transform: 'scale(0.95)'
                    },
                    '70%': {
                      boxShadow: '0 0 0 10px rgba(71, 89, 105, 0)',
                      transform: 'scale(1)'
                    },
                    '100%': {
                      boxShadow: '0 0 0 0 rgba(71, 89, 105, 0)',
                      transform: 'scale(0.95)'
                    }
                  }
                }} />
                <MicIcon
                  fontSize="small"
                  sx={{
                    color: '#475969',
                    animation: 'micPulse 1.5s infinite',
                    '@keyframes micPulse': {
                      '0%': { color: '#475969', transform: 'scale(1)' },
                      '50%': { color: '#374955', transform: 'scale(1.1)' },
                      '100%': { color: '#475969', transform: 'scale(1)' }
                    },
                    zIndex: 1
                  }}
                />
              </>
            ) : (
              <MicIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Select language">
          <IconButton
            size="small"
            onClick={() => setLanguageDialogOpen(true)}
          >
            <LanguageIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* File upload button with conversation files popup */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }} data-files-popup>
          <PermissionGate permission="conversation.upload.files">
            <FileUploadLabel htmlFor="file-upload">
              <Tooltip title="Upload files or images to ask questions about">
                <IconButton
                  component="span"
                  size="small"
                  sx={{
                    color: (files?.length || 0) > 0 ? 'primary.main' : 'inherit',
                  }}
                >
                  <Badge
                    badgeContent={files?.length || 0}
                    color="primary"
                    invisible={(files?.length || 0) === 0}
                  >
                    <AttachFileIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
            </FileUploadLabel>
          </PermissionGate>

          {/* Markdown Import Button */}
          <PermissionGate permission="conversation.import.md">
            <MarkdownFileImport
              onImport={handleMarkdownImport}
              taskType={new URLSearchParams(window.location.search).get('task')}
              buttonText="Import MD"
              buttonVariant="text"
              buttonSize="small"
            />
          </PermissionGate>

          {/* Conversation files button */}
          {conversationFiles.length > 0 && (
            <Tooltip title={`View ${conversationFiles.length} conversation files`}>
              <IconButton
                size="small"
                onClick={() => setShowFilesPopup(!showFilesPopup)}
                sx={{
                  ml: 0.5,
                  color: showFilesPopup ? 'primary.main' : 'text.secondary',
                }}
              >
                <Badge
                  badgeContent={conversationFiles.length}
                  color="secondary"
                  max={99}
                >
                  <DescriptionIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {/* Files popup */}
          {showFilesPopup && (
            <Paper
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                mt: 1,
                width: 320,
                maxHeight: 400,
                overflow: 'auto',
                zIndex: 1300,
                border: '1px solid rgba(0, 0, 0, 0.12)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Conversation Files ({conversationFiles.length})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Files attached to this conversation
                </Typography>
              </Box>

              {loadingFiles ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <List dense sx={{ py: 0 }}>
                  {conversationFiles.map((file, index) => (
                    <ListItem
                      key={file.id || index}
                      sx={{
                        borderBottom: index < conversationFiles.length - 1 ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {file.content_type?.startsWith('image/') ? (
                          <Box
                            component="img"
                            src={file.url}
                            alt={file.original_filename}
                            sx={{
                              width: 32,
                              height: 32,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid rgba(0, 0, 0, 0.12)'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : (
                          <DescriptionIcon color="primary" sx={{ fontSize: 20 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {file.original_filename || file.filename || 'Unknown file'}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {file.file_size ? `${(file.file_size / 1024 / 1024).toFixed(1)} MB` : ''} •
                            {file.created_at ? new Date(file.created_at).toLocaleDateString() : ''}
                          </Typography>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={() => window.open(file.url, '_blank')}
                        sx={{ ml: 1 }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              )}

              <Box sx={{ p: 1, borderTop: '1px solid rgba(0, 0, 0, 0.12)', textAlign: 'center' }}>
                <Button
                  size="small"
                  onClick={() => setShowFilesPopup(false)}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Close
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
      <Grid container spacing={0} alignItems="flex-end" sx={{ width: '100%', maxWidth: '100%' }}>
        <Grid item xs>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <IsolatedChatInput
              ref={chatInputRef}
              externalValue={message}
              placeholder={isListening ? `Listening in ${availableLanguages.find(lang => lang.code === selectedLanguage)?.name || 'selected language'}...` : "How can I help you today?"}
              onSubmit={(e, inputValue) => {
                // Stop speech recognition if active when sending with Enter
                if (isListening) {
                  toggleListening();
                }
                forceHideSpeechAnimation();
                handleMessageSend(e, inputValue);
              }}
              InputProps={{
                endAdornment: (
                  <>
                    {(localIsStreaming || streamingRef.current.isStreaming) && (
                      <Tooltip title="Stop generating" placement="left">
                        <IconButton
                          onClick={() => {
                            console.log('Stopping stream...');

                            // Get the current content before stopping
                            const currentContent = streamingRef.current.content || localStreamingContent;

                            // Call the abort function if available
                            if (streamingRef.current.abortFunction) {
                              streamingRef.current.abortFunction();
                            }

                            // Also try the hook's cancelStream as backup
                            cancelStream();

                            // If we have content, add it as an assistant message
                            if (currentContent && currentContent.trim()) {
                              const assistantMessage = {
                                role: 'assistant',
                                content: currentContent,
                                createdAt: new Date().toISOString(),
                                wasStopped: true // Mark that this was stopped by user
                              };

                              setConversation(prev => {
                                if (!prev) return prev;
                                return {
                                  ...prev,
                                  messages: [...(prev.messages || []), assistantMessage]
                                };
                              });
                            }

                            // Reset streaming state
                            streamingRef.current = {
                              isStreaming: false,
                              content: '',
                              lastUpdated: Date.now(),
                              abortFunction: null
                            };
                            setLocalIsStreaming(false);
                            setLocalStreamingContent('');

                            showNotification({
                              message: 'Response generation stopped',
                              type: 'info'
                            });
                          }}
                          color="error"
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          <StopIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Edit Artifact button — proposes surgical patches */}
                    <Tooltip title="Edit artifact (propose changes)" placement="top">
                      <span>
                        <IconButton
                          onClick={(e) => {
                            const inputValue = chatInputRef.current?.getValue() || '';
                            if (inputValue.trim()) {
                              handleProposePatch(e, inputValue);
                            }
                          }}
                          disabled={loading || patchLoading || !conversation}
                          size="small"
                          sx={{
                            mr: 0.5,
                            color: 'text.secondary',
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {patchLoading ? <CircularProgress size={18} /> : <EditIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={(e) => {
                        // Get value from IsolatedChatInput ref
                        const inputValue = chatInputRef.current?.getValue() || '';
                        if (isListening) {
                          toggleListening();
                        }
                        forceHideSpeechAnimation();
                        handleMessageSend(e, inputValue);
                      }}
                      disabled={loading}
                      sx={{
                        minWidth: 'auto',
                        padding: '4px 10px 4px',
                        '&.Mui-disabled': {
                          opacity: 0.5
                        },
                        '&:hover': {
                          background: 'transparent',
                          '& .MuiSvgIcon-root': {
                            transform: 'scale(1.5)',
                          }
                        },
                        '& .MuiSvgIcon-root': {
                          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                        }
                      }}
                    >
                      {loading ?
                        <CircularProgress size={20} /> :
                        <SendIcon color="primary" fontSize="small" />
                      }
                    </Button>
                  </>
                )
              }}
              sx={{ width: '100%' }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                width: '20px',
                height: '20px',
                cursor: 'nwse-resize',
                pointerEvents: 'auto',
                zIndex: 10,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  right: 1,
                  bottom: 1,
                  width: '16px',
                  height: '16px',
                  background: `linear-gradient(135deg, transparent 50%, #94a3b8 50%)`,
                  opacity: 1,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: 6,
                  bottom: 6,
                  width: '11px',
                  height: '11px',
                  background: `linear-gradient(135deg, transparent 50%, #94a3b8 50%)`,
                  opacity: 1,
                }
              }}
            />
            {/* Floating Proceed Button - Only show for feature-info/change-request-info from create flow */}
            {showProceedButton && (
              <Box sx={{
                textAlign: 'end'
              }}>
                <Tooltip title="Proceed to PRD Workflow" placement="left">
                  <Fab
                    color="primary"
                    aria-label="proceed"
                    onClick={handleProceedToWorkflow}
                    sx={{
                      width: '40px',
                      height: '40px',
                      marginTop:' 0.5rem'
                    }}
                  >
                    <ArrowForwardIcon />
                  </Fab>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Hidden file input */}
      <input
        id="file-upload"
        type="file"
        accept="image/*,.pdf,.docx,.txt,.md,.json,.html,.xlsx"
        onChange={handleFileSelect}
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
      />
    </form>
  );

  // Render appropriate Split View based on conversation task
  const renderSplitView = () => {
    if (!conversation) return null;

    // If there are no messages in the conversation, just render the chat container
    if (!conversation.messages || conversation.messages.length === 0) {
      return (
        <ChatContainer>
          <MessageList ref={messageListRef}>
            {renderMessages()}
          </MessageList>
          <InputContainer>
            {renderChatInput()}
          </InputContainer>
        </ChatContainer>
      );
    }

    // Get task ID from conversation or URL parameters as fallback
    const getTaskId = () => {
      if (conversation?.taskId) {
        console.log('Conversation.js: Using taskId from conversation object:', conversation.taskId);
        return conversation.taskId;
      }
      // Check snake_case field name (Python backend returns task_id)
      if (conversation?.task_id) {
        console.log('Conversation.js: Using task_id (snake_case) from conversation object:', conversation.task_id);
        return conversation.task_id;
      }
      // Fallback to URL parameter
      const urlSearchParams = new URLSearchParams(window.location.search);
      const urlTaskId = urlSearchParams.get('task');
      console.log('Conversation.js: Using taskId from URL parameter:', urlTaskId);
      return urlTaskId;
    };

    // Ensure conversation object has taskId for legacy compatibility
    if (conversation && !conversation.taskId) {
      const fallbackTaskId = getTaskId();
      if (fallbackTaskId) {
        console.log('Conversation.js: Adding missing taskId to conversation object:', fallbackTaskId);
        conversation.taskId = fallbackTaskId;
      }
    }

    // Function to update conversation - accepts either conversationId (for backend fetch) or updated messages (for immediate local update)
    const handleConversationUpdate = async (conversationIdOrMessages, updatedMessages = null) => {
      try {
        // If updatedMessages is provided, update locally immediately (for RHS edit sync to LHS)
        if (updatedMessages && Array.isArray(updatedMessages)) {
          setConversation(prev => ({
            ...prev,
            messages: updatedMessages
          }));
          console.log('✅ Conversation.js: Updated conversation messages locally');
          return;
        }

        // Otherwise fetch from backend (original behavior)
        const updatedConversation = await conversationService.getConversation(conversationIdOrMessages);
        if (updatedConversation) {
          setConversation(updatedConversation);
        }
      } catch (error) {
        console.error('Error reloading conversation:', error);
      }
    };

    const commonProps = {
      conversation,
      projectId,
      taskId: getTaskId(),
      conversationId: conversation?._id,
      isStreaming: localIsStreaming || streamingRef.current.isStreaming,
      streamingContent: streamingRef.current.isStreaming ? streamingRef.current.content : localStreamingContent,
      onConversationUpdate: handleConversationUpdate
    };

    // Debug: Log the final commonProps being passed to split views
    console.log('Conversation.js: commonProps for split views:', {
      taskId: commonProps.taskId,
      conversationId: commonProps.conversationId,
      isStreaming: commonProps.isStreaming,
      hasConversation: !!commonProps.conversation
    });

    const chatContainer = (
      <ChatContainer>
        <MessageList ref={messageListRef}>
          {/* Show visual links panel for wireframes, designs, or design-system tasks */}
          {['wireframes', 'designs', 'design-system'].includes(getTaskId()) && projectId && conversation?._id && (
            <VisualLinksPanel
              projectId={projectId}
              taskType={getTaskId()}
              conversationId={conversation._id}
              onPageEdited={handleVisualPageEdited}
              refreshKey={visualPageEditKey}
            />
          )}
          {renderMessages()}
        </MessageList>
        <InputContainer>
          {renderChatInput()}
        </InputContainer>

        {/* Ready button - shown on appropriate pages */}
        {/*(
          <Button
            variant="contained"
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000,
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              bgcolor: '#d94d85',
              '&:hover': {
                bgcolor: '#c23e72',
              },
              fontWeight: 'bold',
              px: 3
            }}
            onClick={handleSaveDocument}
          >
            Ready!
          </Button>
        )*/}
      </ChatContainer>
    );

    // Debug: Log which split view is being selected
    console.log('Conversation.js: Selecting split view for taskId:', conversation.taskId);

    switch (conversation.taskId) {
      case 'user-stories':
        return (
          <SplitViewUserStories {...commonProps}>
            {chatContainer}
          </SplitViewUserStories>
        );
      case 'user-roles':
        return (
          <SplitViewUserRoles {...commonProps}>
            {chatContainer}
          </SplitViewUserRoles>
        );
      case 'wireframes':
        return (
          <SplitViewWireframes {...commonProps} refreshKey={visualPageEditKey} lastEditedPage={lastEditedPage}>
            {chatContainer}
          </SplitViewWireframes>
        );
      case 'design-references':
        return (
          <SplitViewDesignReferences {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewDesignReferences>
        );
      case 'designs':
      case 'design-system':
        return (
          <SplitViewDesigns {...commonProps} refreshKey={visualPageEditKey}>
            {chatContainer}
          </SplitViewDesigns>
        );
      case 'process-flows':
        return (
          <SplitViewProcessFlows {...commonProps}>
            {chatContainer}
          </SplitViewProcessFlows>
        );
      case 'architecture':
        return (
          <SplitViewArchitecture {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewArchitecture>
        );
      case 'dev-guidelines':
        return (
          <SplitViewDevelopment {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewDevelopment>
        );
      case 'field-validations':
        return (
          <SplitViewFieldValidations {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewFieldValidations>
        );
      case 'tech-stack':
        return (
          <SplitViewTechStack {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewTechStack>
        );
      case 'unit-testing':
        return (
          <SplitViewUnitTesting {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewUnitTesting>
        );
      case 'user-permissions':
        return (
          <SplitViewUserPermissions {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewUserPermissions>
        );
      case 'security-testing':
        return (
          <SplitViewSecurityTesting {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewSecurityTesting>
        );
      case 'security-guidelines':
        return (
          <SplitViewSecurityGuidelines {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewSecurityGuidelines>
        );
      case 'cicd-pipeline':
        return (
          <SplitViewCICDPipeline {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewCICDPipeline>
        );
      case 'functional-testing':
        return (
          <SplitViewFunctionalTesting {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewFunctionalTesting>
        );
      case 'automated-testing':
        return (
          <SplitViewAutomatedTesting {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewAutomatedTesting>
        );
      case 'automation-testing':
        return (
          <SplitViewAutomationTesting {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewAutomationTesting>
        );
      case 'performance-testing':
        return (
          <SplitViewPerformanceTesting {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewPerformanceTesting>
        );
      case 'git-guidelines':
        return (
          <SplitViewGitGuidelines {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewGitGuidelines>
        );
      case 'version-control':
        return (
          <SplitViewVersionControl {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewVersionControl>
        );
      case 'api-guidelines':
        return (
          <SplitViewAPIGuidelines {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewAPIGuidelines>
        );
      case 'project-info':
        return (
          <SplitViewProjectInfoConversation {...commonProps}>
            {chatContainer}
          </SplitViewProjectInfoConversation>
        );
      case 'new-feature':
        // Temporarily use regular conversation view for demo
        return (
          <SplitViewConversation {...commonProps}>
            {chatContainer}
          </SplitViewConversation>
        );
      case 'integration':
        return (
          <SplitViewIntegration {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewIntegration>
        );

      /* Original split view - commented for demo
      return (
        <SplitViewProjectInfoConversation {...commonProps}>
          {chatContainer}
        </SplitViewProjectInfoConversation>
      );
      */
      case 'chat-prd':
      case 'aws-deployment':
      case 'deployment':
      case 'cloud-deployment':
      case 'infrastructure':
        return (
          <SplitViewDeployment {...commonProps} projectId={projectId}>
            {chatContainer}
          </SplitViewDeployment>
        );
      case 'extract-prd':
      case 'features':
      case 'design-guidelines':
      case 'testing':
        return (
          <SplitViewConversation {...commonProps}>
            {chatContainer}
          </SplitViewConversation>
        );
      default:
        // Debug: Log unhandled task IDs for future implementation
        if (conversation.taskId) {
          console.warn('Conversation.js: Using default SplitViewConversation for unhandled taskId:', conversation.taskId);
        }
        return (
          <SplitViewConversation {...commonProps}>
            {chatContainer}
          </SplitViewConversation>
        );
    }
  };

  return (
    <>
    <ConversationLayout
      project={project}
      conversation={conversation}
      onSaveDocument={handleSaveDocument}
      onNewConversation={handleNewConversation}
    >
      {/* Speech Listening Animation - displays in center of screen when active */}

      {isListening && (
        <div className="speech-animation-wrapper" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1500,
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 0 30px rgba(71, 89, 105, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          animation: 'pulseAnimation 2s infinite'
        }}>
          <style>
            {`
              @keyframes pulseAnimation {
                0% { box-shadow: 0 0 10px rgba(71, 89, 105, 0.5); transform: translate(-50%, -50%) scale(0.95); }
                50% { box-shadow: 0 0 30px rgba(71, 89, 105, 0.7); transform: translate(-50%, -50%) scale(1.05); }
                100% { box-shadow: 0 0 10px rgba(71, 89, 105, 0.5); transform: translate(-50%, -50%) scale(0.95); }
              }
            `}
          </style>
          <SpeechListeningAnimation
            active={isListening}
            size="large"
            color="#475969"
            style={{
              zIndex: 1300,
              position: 'absolute',
              opacity: 1,
              visibility: 'visible'
            }}
          />
        </div>
      )}

      <Box sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {conversation && renderSplitView()}
        </Box>
      </Box>

      {/* Speech listening animation - centered on screen */}
      <SpeechListeningAnimation
        active={isListening}
        size="large"
        color="#475969"
      />

      {/* File Upload Dialog */}
      <AppDialog
        open={filesDialogOpen}
        onClose={() => setFilesDialogOpen(false)}
        title={files.length > 0 ? "Upload file" : "Manage attached files"}
        primaryActionText={files.length > 0 ? "Upload" : "Close"}
        onPrimaryAction={files.length > 0 ? handleFileUpload : () => setFilesDialogOpen(false)}
        maxWidth="70vw"
      >
        <Box sx={{ p: 5 }}>
          {/* Currently Selected Files */}
          {files.length > 0 && (
            <>
              <Typography variant="subtitle1" color="primary" gutterBottom sx={{ fontWeight: "bold !important" }} fontSize={16}>
                Selected files to upload:
              </Typography>

              <List sx={{
                maxHeight: '200px',
                overflow: 'auto',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: 1,
                mb: 2
              }}>
                {files.filter(file => file && typeof file === 'object').map((file, index) => (
                  <ListItem key={index}
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete" size="small"
                        onClick={() => {
                          const newFiles = [...files];
                          newFiles.splice(index, 1);
                          setFiles(newFiles);
                          if (newFiles.length === 0) {
                            setFilesDialogOpen(false);
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      {file.type && file.type.includes('image') ?
                        <Box component="img"
                          src={URL.createObjectURL(file)}
                          alt={file.name || 'Uploaded file'}
                          sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                        /> :
                        <DescriptionIcon color="primary" />
                      }
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name || 'Unknown file'}
                      secondary={`${((file.size || 0) / 1024 / 1024).toFixed(2)} MB • ${file.type || 'Unknown type'}`}
                      primaryTypographyProps={{ fontWeight: 500, variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {/* Previously Uploaded Files */}
          {uploadedFiles.length > 0 && (
            <>
              <Typography variant="subtitle1" color="secondary" gutterBottom sx={{ fontWeight: "bold !important", mt: files.length > 0 ? 3 : 0 }} fontSize={16}>
                Files ready to send with next message ({uploadedFiles.length}):
              </Typography>

              <List sx={{
                maxHeight: '150px',
                overflow: 'auto',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 1,
                mb: 2,
                bgcolor: 'rgba(0, 0, 0, 0.02)'
              }}>
                {uploadedFiles.filter(uploadedFile => uploadedFile && uploadedFile.file && typeof uploadedFile.file === 'object').map((uploadedFile, index) => (
                  <ListItem key={`uploaded-${index}`}
                    secondaryAction={
                      <IconButton edge="end" aria-label="remove" size="small"
                        onClick={() => {
                          const newUploadedFiles = [...uploadedFiles];
                          newUploadedFiles.splice(index, 1);
                          setUploadedFiles(newUploadedFiles);
                        }}
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            bgcolor: 'error.light',
                            color: 'error.contrastText'
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      {uploadedFile.file.type && uploadedFile.file.type.includes('image') ?
                        <Box component="img"
                          src={URL.createObjectURL(uploadedFile.file)}
                          alt={uploadedFile.file.name || 'Uploaded file'}
                          sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                        /> :
                        <DescriptionIcon color="secondary" />
                      }
                    </ListItemIcon>
                    <ListItemText
                      primary={uploadedFile.file.name || 'Unknown file'}
                      secondary={`${((uploadedFile.file.size || 0) / 1024 / 1024).toFixed(2)} MB • Uploaded ${uploadedFile.uploadedAt?.toLocaleTimeString?.() || 'Unknown time'}`}
                      primaryTypographyProps={{ fontWeight: 500, variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="primary" gutterBottom fontSize={16} sx={{ mt: 4, fontWeight: "bold !important" }}>
              Message to include with files:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Add a message to accompany your files (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleFileUpload();
                }
              }}
              variant="outlined"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px !important'
                },
                '& .MuiInputBase-input': {
                  fontSize: '16px'
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '15px'
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: "12px !important" }}>
              Your files and message will be added to the conversation as knowledge
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            border: '1px dashed rgba(0,0,0,0.2)',
            borderRadius: 1,
            p: 4,
            bgcolor: 'rgba(0,0,0,0.01)',
            mt: 4,
          }}>
            <Typography variant="subtitle1" color="primary" gutterBottom fontSize={16} sx={{ fontWeight: "bold !important" }}>
              Add more files:
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon />}
              size="small"
              component="label"
              sx={{ alignSelf: 'flex-start' }}
            >
              Select File
              <input
                id="file-upload-dialog"
                type="file"
                accept="image/*,.pdf,.doc,.docx,.txt,.md,.csv,.json,.xml"
                onChange={handleFileSelect}
                hidden
              />
            </Button>
            <Typography variant="caption" color="text.secondary">
              Supported formats: Images, PDF, DOC, DOCX, TXT, MD, CSV, JSON, XML (Max 5MB, one file at a time)
            </Typography>
          </Box>
        </Box>
      </AppDialog>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        maxWidth={false}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 1,
            width: '90%',
            height: '90%',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'hidden',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
            pb: '14px',
            pt: '14px',
            pl: 5,
            pr: '10px',
            background: 'white'
          }}
        >
          <Typography variant="h6" fontWeight={600} sx={{ color: '#212529', fontSize: '1.1rem' }}>
            Provide Feedback
          </Typography>
          <IconButton onClick={() => setFeedbackDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 4, py: 5, px: 5, bgcolor: 'white' }}>
          <Box sx={{ px: 0, py: 0, bgcolor: '#fff' }}>
            <DialogContentText sx={{ mb: 3, fontSize: '1rem', color: '#212529' }}>
              How was the assistant's response? Your feedback helps us improve.
            </DialogContentText>

            <Box sx={{ position: 'relative', mb: 4 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="What could be improved about this response? (Optional)"
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                variant="outlined"
                InputProps={{
                  sx: {
                    resize: 'vertical',
                  }
                }}
                sx={{

                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    padding: '0 !important',
                    paddingBottom: '0px !important',
                    fontSize: '16px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    lineHeight: '1.6',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: '#f3f4f6',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      '& fieldset': {
                        borderColor: '#94a3b8',
                      }
                    },

                    '&.Mui-focused': {
                      backgroundColor: 'white',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      '& fieldset': {
                        borderColor: '#4d5666',
                        borderWidth: '1px'
                      }
                    },
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                      borderWidth: '1px',
                      transition: 'border-color 0.3s ease'
                    },
                    '& textarea': {
                      resize: 'vertical',
                      maxHeight: 'calc(100vh - 350px) !important',
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        backgroundColor: 'transparent',
                      },
                      '&::-webkit-scrollbar-track': {
                        borderRadius: '10px',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        marginTop: '5px',
                        marginBottom: '5px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        borderRadius: '10px',
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.4)',
                        },
                      },
                      '&::-webkit-scrollbar-corner': {
                        backgroundColor: 'transparent',
                      }
                    }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    fontWeight: 400,
                    color: '#212529',
                    padding: '16px',
                    paddingBottom: '16px !important',
                    '&::placeholder': {
                      color: '#6c757d',
                      opacity: 0.8,
                      fontSize: '15px'
                    }
                  }
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  width: '16px',
                  height: '16px',
                  cursor: 'nwse-resize',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    right: 1,
                    bottom: 1,
                    width: '14px',
                    height: '14px',
                    background: `linear-gradient(135deg, transparent 50%, #94a3b8 50%)`,
                    opacity: 0.7,
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: 5,
                    bottom: 5,
                    width: '10px',
                    height: '10px',
                    background: `linear-gradient(135deg, transparent 50%, #94a3b8 50%)`,
                    opacity: 0.7,
                  }
                }}
              />
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<StarBorderIcon />}
                  sx={{
                    borderRadius: '4px',
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: '#dc3545',
                    color: '#dc3545',
                    '&:hover': {
                      borderColor: '#a02d3a',
                      color: '#a02d3a',
                      backgroundColor: 'rgba(220, 53, 69, 0.04)'
                    }
                  }}
                  onClick={() => {
                    showNotification({
                      message: 'Rating saved',
                      type: 'info'
                    });
                  }}
                >
                  Not Helpful
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<StarIcon />}
                  sx={{
                    borderRadius: '4px',
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: '#1976d2',
                    color: '#475969',
                    '&:hover': {
                      borderColor: '#115293',
                      color: '#115293',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                  onClick={() => {
                    showNotification({
                      message: 'Rating saved',
                      type: 'info'
                    });
                  }}
                >
                  Helpful
                </Button>
              </Box>

              <Button
                variant="contained"
                sx={{
                  bgcolor: '#4d5666',
                  color: 'white',
                  px: 4,
                  py: 1,
                  '&:hover': {
                    bgcolor: '#343a40',
                  },
                  borderRadius: '4px',
                  textTransform: 'none',
                  fontWeight: 500
                }}
                onClick={() => {
                  showNotification({
                    message: 'Thank you for your feedback!',
                    type: 'success'
                  });
                  setFeedbackDialogOpen(false);
                  setFeedbackMessage('');
                }}
              >
                Submit Feedback
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Language Selection Dialog */}
      <AppDialog
        open={languageDialogOpen}
        onClose={() => setLanguageDialogOpen(false)}
        title="Select Voice Input Language"
        maxWidth='70vw'
      >
        <DialogContentText sx={{ mb: 2, px: 5, pt: 3 }}>
          Choose the language you want to use for voice input:
        </DialogContentText>

        <List sx={{ minWidth: 250, px: 5 }}>
          {availableLanguages.map((language) => (
            <ListItem
              key={language.code}
              button
              onClick={() => {
                setSelectedLanguage(language.code);

                if (isListening && speechRecognition) {
                  speechRecognition.stop();

                  setTimeout(() => {
                    speechRecognition.lang = language.code;
                    speechRecognition.start();

                    showNotification({
                      message: `Now listening in ${language.name}...`,
                      type: "info"
                    });
                  }, 300);
                }

                setLanguageDialogOpen(false);
              }}
              selected={selectedLanguage === language.code}
              sx={{
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(33, 150, 243, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.12)',
                  }
                }
              }}
            >
              <ListItemText primary={language.name} />
              {selectedLanguage === language.code && <MicIcon color="primary" sx={{ ml: 1 }} />}
            </ListItem>
          ))}
        </List>
      </AppDialog>


      {/* ── Artifact Patch Confirmation Dialog ── */}
      <Dialog
        open={!!patchProposal}
        onClose={handleRejectPatch}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Proposed Changes
        </DialogTitle>
        <DialogContent dividers>
          {patchProposal && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {patchProposal.summary}
              </Typography>
              {patchProposal.proposals.map((p, idx) => (
                <Box key={idx} sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Chip label={p.section_title} size="small" sx={{ mb: 1 }} />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>Remove:</Typography>
                      <Typography variant="body2" sx={{
                        fontFamily: 'monospace', fontSize: '0.8rem', mt: 0.5,
                        bgcolor: 'error.50', p: 1, borderRadius: 0.5,
                        textDecoration: 'line-through', color: 'error.dark',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                      }}>
                        {p.old_str}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>Replace with:</Typography>
                      <Typography variant="body2" sx={{
                        fontFamily: 'monospace', fontSize: '0.8rem', mt: 0.5,
                        bgcolor: 'success.50', p: 1, borderRadius: 0.5,
                        color: 'success.dark',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                      }}>
                        {p.new_str}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleRejectPatch} color="inherit" disabled={patchLoading}>
            Reject
          </Button>
          <Button
            onClick={() => {
              handleRejectPatch();
              // Send as normal chat instead
              const msg = patchProposal?.pendingMessage;
              if (msg) {
                setTimeout(() => handleMessageSend(null, msg), 100);
              }
            }}
            color="primary"
            variant="outlined"
            disabled={patchLoading}
          >
            Send as Chat
          </Button>
          <Button
            onClick={handleApplyPatch}
            color="primary"
            variant="contained"
            disabled={patchLoading}
            startIcon={patchLoading ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            Apply Changes
          </Button>
        </DialogActions>
      </Dialog>

    </ConversationLayout>

    {/* Global Search Dialog (Cmd+K) */}
    <React.Suspense fallback={null}>
      <SearchDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        projectId={projectId}
      />
    </React.Suspense>
  </>
  );
};

export default Conversation;
